import { z } from 'zod';

import { boolParam, listQuerySchema, sortDirSchema } from './common.js';
import {
  PLACEMENT_SPECS,
  placementAllowsAspect,
  productionPlacementSchema,
  type ProductionPlacement,
} from './production-placements.js';

/**
 * PRODÜKSİYON — "Reklam videosu" motorunun SÖZLEŞMESİ (Kreatif stüdyo Faz 2,
 * 1 Eki 2026). Sonraki fazlar (akıl, storyboard, çekim, kurgu, arayüz, sohbet)
 * bu dosyaya yazıyor; adlar SABİT.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Ne bu
 * ═══════════════════════════════════════════════════════════════════════════
 * Bir reklam videosu artık TEK bir stokastik API çağrısı değil, kalıcı
 * aşamalardan geçen bir prodüksiyon (KREATIF-STUDYO-PLANI.md §1.3, §2):
 * anla → (sorular) → konseptler → senaryo → çekim listesi → storyboard →
 * ONAY → çekim + QA → ses → kurgu → son kart → çıktılar. Her aşamanın çıktısı
 * DB'de (`creative_productions` ve üç çocuk tablosu) ve R2'de durur; yeniden
 * deneme kaldığı yerden sürer.
 *
 * İç ad **prodüksiyon** (`CreativeProduction`); arayüzde kelime "Reklam
 * videosu" — kullanıcı kararı: bu bir REKLAM KREATİFİ, sinema filmi değil.
 * Akışın İLK sorusu boyut (`aspect`, zorunlu) ve her çekim o boyutta YEREL
 * kompozisyonla kuruluyor; diğer boyutlar opsiyonel ek çıktı.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Bu dosyada PROMPT ve UÇ YOK — barrel'da ve tarayıcıya gidiyor
 * ═══════════════════════════════════════════════════════════════════════════
 *   · Model rotası (hangi çekim türü hangi fal ucuna) → `@mentorivo/shared/production-routes`
 *   · USD ve kredi hesabı                         → `@mentorivo/shared/production-pricing`
 * İkisi de alt yolda, yalnız sunucu. Derlenmiş sağlayıcı prompt'u hiçbir API
 * yanıtına ve `Creative.inputParams`a GİRMEZ; kullanıcı çekim SPEC'ini
 * görür ve düzenler, prompt sunucuda spec'ten derlenir.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Sohbet eşitliği (kullanıcı kararı)
 * ═══════════════════════════════════════════════════════════════════════════
 * Prodüksiyon sayfasında yapılabilen her şey AI sohbetinden de yapılabilecek
 * (Faz 8). Bu yüzden istek şemaları HTTP'ye özgü hiçbir şey taşımıyor (yol
 * parametresi yok, başlık yok) — ajan aracı aynı şemayı aynı servise verir.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Boyut, süre, fotoğraf
// ═══════════════════════════════════════════════════════════════════════════

/// Sıra arayüzün sırası: dikey tam ekran → dikey gönderi → kare → yatay.
export const PRODUCTION_ASPECTS = ['9:16', '4:5', '1:1', '16:9'] as const;
export type ProductionAspect = (typeof PRODUCTION_ASPECTS)[number];
export const productionAspectSchema = z.enum(PRODUCTION_ASPECTS);

/**
 * Teslim boyutu (genişlik × yükseklik). Modellerin çıktısı buna ffmpeg ile
 * ölçeklenip KIRPILIR — modeller girdi görüntüsünün oranını kopyalıyor
 * (nano-banana 1536×2752 karesinden H3 1076×1928 üretti) ve QA'da %2'nin
 * üstündeki oran sapması kritik kusur (REPORT §2.1/2).
 */
export const ASPECT_PIXELS: Readonly<Record<ProductionAspect, readonly [width: number, height: number]>> = {
  '9:16': [1080, 1920],
  '4:5': [1080, 1350],
  '1:1': [1080, 1080],
  '16:9': [1920, 1080],
};

/// `9:16` → `9x16` — rendition anahtarında ve dosya adında `:` yasak (R2
/// anahtarı geçerli ama BullMQ iş kimliği ve indirme adı değil).
export function aspectSlug(aspect: ProductionAspect): string {
  return aspect.replace(':', 'x');
}

/// 6 = bumper, 15/30/60 platform standartları. Ara değerler (10, 20, 45)
/// Stories ve TikTok'un önerdiği bantlar.
export const PRODUCTION_DURATIONS = [6, 10, 15, 20, 30, 45, 60] as const;
export type ProductionDuration = (typeof PRODUCTION_DURATIONS)[number];
export const productionDurationSchema = z.literal(PRODUCTION_DURATIONS);
/// Süre de yerleşim de verilmediyse planlamanın varsayıldığı süre (sn) —
/// rezerv tahmini (`planningDurationFor`) ve kesim doğrulaması aynı sayıyı okur.
export const PRODUCTION_FALLBACK_DURATION = 15;

/**
 * Ürün fotoğrafı tavanı. Faz 1 ölçümü: tek fotoğraftan çok açılı referans
 * üretimi etiketi BOZUYOR (qwen multiple-angles yatay açıyı uygulamıyor) —
 * en güvenli yol kullanıcının kendi açı fotoğrafları. Altıdan fazlası
 * referans setine (H3 r2v ≤9) sığmıyor ve QA ızgarasını şişiriyor.
 */
export const PRODUCTION_MAX_PHOTOS = 6;
export const PRODUCTION_NOTE_MAX = 1000;

/**
 * REKLAMIN HEDEFİ (opsiyonel, Dalga 4) — çıktı biçimi kararının (strateji
 * adımı) ve fikirlerin girdisi: farkındalık reklamı ile indirim reklamı aynı
 * kurulmaz. Yoksa AI ürün ve yerleşimden çıkarır.
 *   awareness    marka/ürün bilinirliği (geniş kitle, akılda kalan açılış)
 *   traffic      siteye/ürün sayfasına tıklama
 *   conversion   satın alma (teklif + net CTA)
 *   retargeting  ürünü görmüş kitleye yeniden (itiraz kırma, sosyal kanıt)
 *   launch       yeni ürün lansmanı
 *   promo        kampanya/indirim dönemi
 */
export const PRODUCTION_GOALS = [
  'awareness',
  'traffic',
  'conversion',
  'retargeting',
  'launch',
  'promo',
] as const;
export type ProductionGoal = (typeof PRODUCTION_GOALS)[number];
export const productionGoalSchema = z.enum(PRODUCTION_GOALS);

/**
 * VARYANT ANAHTARLARI — bir prodüksiyonun 1..5 reklamı (`deliverablePlanSchema`).
 * `v1` BİRİNCİL: seçilen fikrin ana reklamı; geçmiş ekranı her prodüksiyondan
 * TEK kart gösterir ve o kart en son kurgunun `v1` master'ı.
 */
export const PRODUCTION_VARIANT_KEYS = ['v1', 'v2', 'v3', 'v4', 'v5'] as const;
export type ProductionVariantKey = (typeof PRODUCTION_VARIANT_KEYS)[number];
export const productionVariantKeySchema = z.enum(PRODUCTION_VARIANT_KEYS);
export const PRODUCTION_PRIMARY_VARIANT: ProductionVariantKey = 'v1';
export const PRODUCTION_MAX_VARIANTS = PRODUCTION_VARIANT_KEYS.length;

// ═══════════════════════════════════════════════════════════════════════════
// Prodüksiyon türü — reklam videosu / GÖRSEL KAMPANYA (Dalga 5, 2 Eki 2026)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Kullanıcı kararı (plan §4, 2 Eki): "Görsel türüne yeni sekme — Prompt · Ürün
 * · **Kampanya seti** (seri / karusel / banner seti); reklam videosuyla aynı
 * sade akış (Fikir → Taslak → Hazır), AYNI prodüksiyon motoru ve sayfası".
 *
 *   VIDEO      reklam videosu (bugünkü motor)
 *   IMAGE_SET  görsel kampanya seti — anla → fikir → KARE PLANI (`SET_PLAN`) →
 *              referans → FİNAL KARELER (`FRAMES`: anahtar kare hattı, yazısız
 *              plaka) → ONAY → tipografi + boyut uyarlama (`FINISH`) → teslim.
 *              Pahalı kısım (kareler) onaydan ÖNCE storyboard rezerviyle
 *              üretilir; onayın kalan bedeli tipografi + boyut uyarlama + QA
 *              (`production-pricing` → `estimateImageSetUsd`).
 * Teslim `Creative` satırı: VIDEO → `AD_VIDEO`, IMAGE_SET → `AD_IMAGE`; ikisinde
 * de `productionId` + `rendition`, geçmiş her setten TEK kart (işaretçi
 * `CreativeProduction.masterCreativeId` = setin KAPAĞI, `productionCoverRendition`).
 */
export const ProductionKind = { VIDEO: 'VIDEO', IMAGE_SET: 'IMAGE_SET' } as const;
export type ProductionKind = (typeof ProductionKind)[keyof typeof ProductionKind];
export const PRODUCTION_KINDS: readonly ProductionKind[] = Object.values(ProductionKind);
export const productionKindSchema = z.enum(ProductionKind);

/**
 * Görsel kampanyanın biçimi.
 *   SERIES      aynı fikrin 5–10 karelik TUTARLI serisi (her kare ayrı reklam
 *               görseli; aynı ışık, palet, ürün kimliği)
 *   CAROUSEL    hikâye anlatan 3–10 kartlık karusel (kartlar bir anlatıyla
 *               sıralı; son kart çağrı)
 *   BANNER_SET  TEK ana görselin (key visual) birden çok reklam boyutu
 *               (`BANNER_SET_SIZES`) — yerleşime göre tipografi
 */
export const ImageSetType = {
  SERIES: 'SERIES',
  CAROUSEL: 'CAROUSEL',
  BANNER_SET: 'BANNER_SET',
} as const;
export type ImageSetType = (typeof ImageSetType)[keyof typeof ImageSetType];
export const IMAGE_SET_TYPES: readonly ImageSetType[] = Object.values(ImageSetType);
export const imageSetTypeSchema = z.enum(ImageSetType);

/// Seri ve karuselin kare sayısı sınırları (kullanıcı kararı, 2 Eki 2026).
export const IMAGE_SET_COUNT_LIMITS: Readonly<
  Record<Exclude<ImageSetType, 'BANNER_SET'>, { min: number; max: number }>
> = {
  SERIES: { min: 5, max: 10 },
  CAROUSEL: { min: 3, max: 10 },
};
/// Bir setin en çok karesi (seri/karusel tavanı; banner setinde tek ana görsel).
export const IMAGE_SET_MAX_FRAMES = 10;

/**
 * Karusel kartının boyutu — Meta karuseli 1:1 ve 4:5 kabul ediyor, LinkedIn
 * karuseli 1:1; dikey tam ekran (9:16) ve yatay (16:9) karusel kartı yok.
 */
export const CAROUSEL_ASPECTS: readonly ProductionAspect[] = ['1:1', '4:5'];

/**
 * Görsel kampanyanın yerleşimleri — yalnız GÖRSEL reklam alan yerleşimler
 * (YouTube in-stream/Shorts ve Reels video ister). Banner seti yerleşimsiz de
 * olabilir: boyutları (`BANNER_SET_SIZES`) yerleşimin yerine geçer.
 */
export const IMAGE_SET_PLACEMENTS: readonly ProductionPlacement[] = [
  'meta_feed',
  'meta_stories',
  'tiktok',
  'linkedin_feed',
  'x_feed',
];

/// Banner boyutunun standart olduğu platform (reklam yöneticisinin önerdiği boyut).
export const BANNER_PLATFORMS = ['meta', 'tiktok', 'google_display', 'linkedin', 'x'] as const;
export type BannerPlatform = (typeof BANNER_PLATFORMS)[number];

/**
 * Yerleşim sınıfı — boyut uyarlamasının (FINISH) tipografi düzeni buna göre:
 *   square / portrait / story / landscape  ürün + başlık + CTA tam düzen
 *   rectangle   (300×250) ürün + kısa başlık + CTA
 *   leaderboard (728×90, 320×50) yatay şerit: logo · başlık · CTA, ürün küçük
 *   skyscraper  (160×600) dikey şerit: ürün üstte, başlık + CTA altta
 */
export const BANNER_LAYOUTS = [
  'square',
  'portrait',
  'story',
  'landscape',
  'rectangle',
  'leaderboard',
  'skyscraper',
] as const;
export type BannerLayout = (typeof BANNER_LAYOUTS)[number];

export interface BannerSetSize {
  /// `<genişlik>x<yükseklik>` — teslim adında da bu (`banner.300x250`).
  id: string;
  width: number;
  height: number;
  /// Prodüksiyon boyutlarından EN YAKINI — teslimin `aspect` alanı ve yeniden
  /// kadrajın kaynağı (ana görsel bu orana en az kırpımla iner).
  nearestAspect: ProductionAspect;
  layout: BannerLayout;
  /**
   * Bu boyutun STANDART olduğu platformlar (reklam yöneticisinin önerdiği /
   * kabul ettiği boyut). Boş = standart dışı (özel boyut; bugün katalogda yok).
   */
  standardFor: readonly BannerPlatform[];
  /// `standardFor` boş değil — platform standardı.
  platformStandard: boolean;
  /// IAB standart görüntülü reklam birimi (Google Görüntülü Reklam Ağı).
  iab: boolean;
  /**
   * Dosya tavanı (bayt) — Google Ads yüklenen görsel reklam 150 KB. `null` =
   * pratikte sınırsız (Meta/TikTok/LinkedIn MB mertebesinde). FINISH kodlamayı
   * bu tavana göre seçer (PNG → kaliteli JPEG/WebP).
   */
  maxBytes: number | null;
}

const IAB_MAX_BYTES = 150 * 1024;

/**
 * BANNER SETİ BOYUT KATALOĞU — kullanıcı listeden seçer (`imageSet.sizes`).
 * Sekizinin de bir platform standardı olduğu işaretli (`platformStandard`);
 * dördü IAB görüntülü reklam birimi (Google Görüntülü Reklam Ağı, 150 KB).
 */
export const BANNER_SET_SIZES = [
  {
    id: '1080x1080',
    width: 1080,
    height: 1080,
    nearestAspect: '1:1',
    layout: 'square',
    standardFor: ['meta', 'linkedin', 'x'],
    platformStandard: true,
    iab: false,
    maxBytes: null,
  },
  {
    id: '1080x1350',
    width: 1080,
    height: 1350,
    nearestAspect: '4:5',
    layout: 'portrait',
    standardFor: ['meta', 'linkedin'],
    platformStandard: true,
    iab: false,
    maxBytes: null,
  },
  {
    id: '1080x1920',
    width: 1080,
    height: 1920,
    nearestAspect: '9:16',
    layout: 'story',
    standardFor: ['meta', 'tiktok'],
    platformStandard: true,
    iab: false,
    maxBytes: null,
  },
  {
    id: '1200x628',
    width: 1200,
    height: 628,
    nearestAspect: '16:9',
    layout: 'landscape',
    standardFor: ['meta', 'google_display', 'linkedin', 'x'],
    platformStandard: true,
    iab: false,
    maxBytes: null,
  },
  {
    id: '300x250',
    width: 300,
    height: 250,
    nearestAspect: '1:1',
    layout: 'rectangle',
    standardFor: ['google_display'],
    platformStandard: true,
    iab: true,
    maxBytes: IAB_MAX_BYTES,
  },
  {
    id: '728x90',
    width: 728,
    height: 90,
    nearestAspect: '16:9',
    layout: 'leaderboard',
    standardFor: ['google_display'],
    platformStandard: true,
    iab: true,
    maxBytes: IAB_MAX_BYTES,
  },
  {
    id: '160x600',
    width: 160,
    height: 600,
    nearestAspect: '9:16',
    layout: 'skyscraper',
    standardFor: ['google_display'],
    platformStandard: true,
    iab: true,
    maxBytes: IAB_MAX_BYTES,
  },
  {
    id: '320x50',
    width: 320,
    height: 50,
    nearestAspect: '16:9',
    layout: 'leaderboard',
    standardFor: ['google_display'],
    platformStandard: true,
    iab: true,
    maxBytes: IAB_MAX_BYTES,
  },
] as const satisfies readonly BannerSetSize[];
export type BannerSizeId = (typeof BANNER_SET_SIZES)[number]['id'];
export const BANNER_SIZE_IDS = BANNER_SET_SIZES.map((size) => size.id) as readonly BannerSizeId[];
export const bannerSizeIdSchema = z.enum(
  BANNER_SIZE_IDS as unknown as readonly [BannerSizeId, ...BannerSizeId[]],
);
/// Formun önseçimi: Meta'nın üç boyutu + Google'ın en çok gösterilen birimi.
export const BANNER_SET_DEFAULT_SIZES: readonly BannerSizeId[] = [
  '1080x1080',
  '1080x1350',
  '1080x1920',
  '300x250',
];

export function bannerSetSize(id: string): BannerSetSize | null {
  return BANNER_SET_SIZES.find((size) => size.id === id) ?? null;
}

/**
 * BANNER SETİNİN ANA GÖRSEL ORANI — seçilen boyutların EN ÇOK paylaştığı
 * en yakın oran (`nearestAspect`); eşitlikte katalog sırası (kare önce).
 * Ana görsel bu oranda üretiliyor ve her boyuta ondan iniyor: çoğunluğun
 * oranında üretmek, yeniden kadraj (ücretli) gereken boyut sayısını en aza
 * indiriyor. Stüdyo formu ve sohbet aracı AYNI fonksiyonu kullanır — sohbet
 * ilk boyutun oranını alıyordu, aynı liste iki yoldan farklı ana görsel,
 * fiyat ve kadraj sayısı veriyordu (denetim bulgusu, 2 Eki 2026). Sırası
 * önemsiz.
 */
export function bannerKeyAspect(sizes: readonly string[]): ProductionAspect {
  const tally = new Map<ProductionAspect, number>();
  for (const id of new Set(sizes)) {
    const size = bannerSetSize(id);
    if (size) tally.set(size.nearestAspect, (tally.get(size.nearestAspect) ?? 0) + 1);
  }
  let best: ProductionAspect = '1:1';
  let bestCount = -1;
  for (const size of BANNER_SET_SIZES) {
    const count = tally.get(size.nearestAspect) ?? 0;
    if (count > bestCount) {
      best = size.nearestAspect;
      bestCount = count;
    }
  }
  return best;
}

/// Banner boyutları katalog sırasında (tekil) — kapak ve teslim sırası iki yoldan aynı.
export function orderedBannerSizes<T extends string>(sizes: readonly T[]): T[] {
  return BANNER_SET_SIZES.map((size) => size.id as string).filter((id): id is T =>
    (sizes as readonly string[]).includes(id),
  );
}

/**
 * Başlatma isteğinin görsel kampanya parçası (`productionStartSchema.imageSet`,
 * `kind: 'IMAGE_SET'` iken ZORUNLU).
 *   SERIES / CAROUSEL  `count` kare (sınırlar `IMAGE_SET_COUNT_LIMITS`), `sizes` yok
 *   BANNER_SET         `sizes` 1–8 tekil boyut (katalogdan), `count` yok — tek
 *                      ana görsel her boyuta uyarlanır
 */
export const imageSetRequestSchema = z
  .object({
    setType: imageSetTypeSchema,
    count: z.number().int().min(1).max(IMAGE_SET_MAX_FRAMES).optional(),
    sizes: z.array(bannerSizeIdSchema).min(1).max(BANNER_SET_SIZES.length).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.setType === 'BANNER_SET') {
      if (!value.sizes?.length) {
        ctx.addIssue({
          code: 'custom',
          path: ['sizes'],
          message: 'Banner seti için en az bir boyut seçin.',
        });
      } else if (new Set(value.sizes).size !== value.sizes.length) {
        ctx.addIssue({ code: 'custom', path: ['sizes'], message: 'Boyutlar tekrar edemez.' });
      }
      if (value.count !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['count'],
          message: 'Banner setinde kare sayısı yok — boyutları seçin.',
        });
      }
      return;
    }
    const limits = IMAGE_SET_COUNT_LIMITS[value.setType];
    if (value.count === undefined || value.count < limits.min || value.count > limits.max) {
      ctx.addIssue({
        code: 'custom',
        path: ['count'],
        message: `Kare sayısı ${limits.min}–${limits.max} olmalı.`,
      });
    }
    if (value.sizes !== undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['sizes'],
        message: 'Boyut listesi yalnız banner setinde.',
      });
    }
  });
export type ImageSetRequest = z.infer<typeof imageSetRequestSchema>;

/**
 * Setin KARE sayısı — kare planının (`imageSetPlanSchema`) ve fiyatın girdisi:
 * seri/karusel istenen sayı, banner seti TEK ana görsel.
 */
export function imageSetFrameCount(request: Pick<ImageSetRequest, 'setType' | 'count'>): number {
  return request.setType === 'BANNER_SET'
    ? 1
    : (request.count ?? IMAGE_SET_COUNT_LIMITS[request.setType].min);
}

/**
 * Setin TESLİM sayısı (kütüphanedeki görsel adedi): seri/karusel kare sayısı,
 * banner seti boyut sayısı. `CreativeProduction.setSize` kolonu bu sayı.
 */
export function imageSetDeliverableCount(request: ImageSetRequest): number {
  return request.setType === 'BANNER_SET'
    ? (request.sizes?.length ?? 0)
    : imageSetFrameCount(request);
}

// ═══════════════════════════════════════════════════════════════════════════
// Durum makinesi — Prisma enum'larının aynası
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Prodüksiyonun KABA durumu — liste, rozet ve süpürücü bunu okur. İnce
 * ilerleme `stage`te, kullanıcıda durulan yer `gate`te.
 *
 *   PLANNING          anla → konsept → senaryo → çekim listesi (LLM)
 *   AWAITING_INPUT    kullanıcıda bir kapı açık (soru, konsept seçimi, senaryo
 *                     düzenleme, başarısız çekim, revizyon rızası)
 *   STORYBOARDING     referans seti + anahtar kareler + QA
 *   AWAITING_APPROVAL storyboard hazır, üretim kredisi rızası bekleniyor
 *   PRODUCING         çekim + QA (pahalı kısım)
 *   FINISHING         ses, kurgu, son kart, çıktılar
 *   DELIVERED / FAILED / CANCELED  terminal
 */
export const ProductionStatus = {
  PLANNING: 'PLANNING',
  AWAITING_INPUT: 'AWAITING_INPUT',
  STORYBOARDING: 'STORYBOARDING',
  AWAITING_APPROVAL: 'AWAITING_APPROVAL',
  PRODUCING: 'PRODUCING',
  FINISHING: 'FINISHING',
  DELIVERED: 'DELIVERED',
  FAILED: 'FAILED',
  CANCELED: 'CANCELED',
} as const;
export type ProductionStatus = (typeof ProductionStatus)[keyof typeof ProductionStatus];
export const productionStatusSchema = z.enum(ProductionStatus);

export const PRODUCTION_TERMINAL_STATUSES: readonly ProductionStatus[] = ['DELIVERED', 'FAILED', 'CANCELED'];
export function isProductionTerminal(status: ProductionStatus): boolean {
  return PRODUCTION_TERMINAL_STATUSES.includes(status);
}

/**
 * Aşamalar — sıra BU DİZİNİN sırası (ilerleme çubuğu ve "geri dön" kararı).
 *
 * GÖRSEL KAMPANYA (`IMAGE_SET`, Dalga 5) iki aşama ekledi, yalnız o türde
 * kullanılır: `SET_PLAN` (kare planı — senaryo + çekim listesinin YERİNE;
 * sırada fikirden sonra, senaryodan önce: planlama evresi, durum PLANNING) ve
 * `FRAMES` (final kareler — anahtar kare hattı, yazısız plaka; storyboard'dan
 * sonra, çekimden önce: durum STORYBOARDING). Video akışı bu iki aşamaya
 * hiç uğramaz; sıranın yeri yalnız `statusForStage`/`stageBefore` kararlarını
 * görsel kampanyada da doğru verdirmek için.
 */
export const ProductionStage = {
  UNDERSTAND: 'UNDERSTAND',
  QUESTIONS: 'QUESTIONS',
  CONCEPTS: 'CONCEPTS',
  SET_PLAN: 'SET_PLAN',
  SCRIPT: 'SCRIPT',
  SHOTLIST: 'SHOTLIST',
  REFS: 'REFS',
  STORYBOARD: 'STORYBOARD',
  FRAMES: 'FRAMES',
  SHOOT: 'SHOOT',
  AUDIO: 'AUDIO',
  EDIT: 'EDIT',
  FINISH: 'FINISH',
  DELIVER: 'DELIVER',
} as const;
export type ProductionStage = (typeof ProductionStage)[keyof typeof ProductionStage];
export const productionStageSchema = z.enum(ProductionStage);
export const PRODUCTION_STAGE_ORDER: readonly ProductionStage[] = Object.values(ProductionStage);

/**
 * Üç mod (plan §2.2):
 *   ASK    — direktör kararı değiştiren 2–4 soru sorar (şık + önizleme karesi)
 *   DECIDE — soru yok; en güçlü konsepti seçer, gerekçesini yazar
 *   DIRECT — kullanıcı konsepti seçer, senaryoyu ve storyboard'u çekim çekim düzenler
 * Onay (üretim kredisi rızası) HER modda var.
 */
export const ProductionMode = { ASK: 'ASK', DECIDE: 'DECIDE', DIRECT: 'DIRECT' } as const;
export type ProductionMode = (typeof ProductionMode)[keyof typeof ProductionMode];
export const productionModeSchema = z.enum(ProductionMode);

/// Kullanıcıda durulan kapı. `null` = motor çalışıyor ya da prodüksiyon terminal.
export const ProductionGate = {
  QUESTIONS: 'QUESTIONS',
  CONCEPT_PICK: 'CONCEPT_PICK',
  SCRIPT_EDIT: 'SCRIPT_EDIT',
  STORYBOARD: 'STORYBOARD',
  /// Bir çekimin bütün kurtarma yolları düştü — kullanıcı seçer
  /// (sadeleştir / çıkar / başka model). Bozuk kare teslim edilmez.
  SHOT_FAILED: 'SHOT_FAILED',
  /// Onaydan sonra maliyetli revizyon — ek kredi rızası.
  REVISION_CONFIRM: 'REVISION_CONFIRM',
} as const;
export type ProductionGate = (typeof ProductionGate)[keyof typeof ProductionGate];
export const productionGateSchema = z.enum(ProductionGate);

/**
 * Çekim türü = MODEL ROTASI sınıfı (REPORT §2.2). Çekim listesi her çekime bir
 * tür verir; rota tablosu (`production-routes`) türden uca gider. Tür ürünün
 * kararı, şablonun değil.
 */
export const ShotKind = {
  HERO_PRODUCT: 'HERO_PRODUCT',
  PRODUCT_ORBIT: 'PRODUCT_ORBIT',
  HANDS_USAGE: 'HANDS_USAGE',
  HUMAN_PERFORMANCE: 'HUMAN_PERFORMANCE',
  MACRO_TEXTURE: 'MACRO_TEXTURE',
  LIQUID_FOOD: 'LIQUID_FOOD',
  VFX_TRANSFORM: 'VFX_TRANSFORM',
  ENVIRONMENT: 'ENVIRONMENT',
  ABSTRACT: 'ABSTRACT',
} as const;
export type ShotKind = (typeof ShotKind)[keyof typeof ShotKind];
export const shotKindSchema = z.enum(ShotKind);

/**
 * Çekimin hâli.
 *   PLANNED      spec yazıldı, anahtar kare yok
 *   KEYFRAMING   anahtar kare üretimi / QA'sı sürüyor
 *   STORYBOARDED anahtar kare kabul edildi (onay bekliyor ya da çekime hazır)
 *   SHOOTING     take'ler üretiliyor / QA'dan geçiyor
 *   READY        geçen bir take seçildi (`chosenTakeId`)
 *   FAILED       bütün kurtarma yolları düştü (kapı `SHOT_FAILED`)
 *   DROPPED      kullanıcı ya da revizyon çıkardı — satır maliyet izi için kalır
 */
export const ProductionShotStatus = {
  PLANNED: 'PLANNED',
  KEYFRAMING: 'KEYFRAMING',
  STORYBOARDED: 'STORYBOARDED',
  SHOOTING: 'SHOOTING',
  READY: 'READY',
  FAILED: 'FAILED',
  DROPPED: 'DROPPED',
} as const;
export type ProductionShotStatus = (typeof ProductionShotStatus)[keyof typeof ProductionShotStatus];
export const productionShotStatusSchema = z.enum(ProductionShotStatus);

/**
 * Take'in hâli.
 *
 * 🔴 `QUEUED` satırı GÖNDERİMDEN ÖNCE açılır, `SUBMITTED` fal `request_id`si
 * yazıldığı anda — tutamaç (`providerHandle`) dolu bir take yeniden
 * denemede YENİDEN GÖNDERİLMEZ, eski iş yoklanır (Faz 0 dersi: ikinci ücret
 * yok). Take'ler yeniden üretilemez (Kling/Gemini/Seedance tohum almıyor) —
 * her çıktı R2'ye arşivlenir.
 *
 *   GENERATED  çıktı arşivlendi, QA bekliyor
 *   PASSED     QA geçti (marj ≥ 2)
 *   WEAK       QA geçti ama marj ≤ 1 — "zayıf geçiş" (REPORT §7.5): başka take
 *              aranır, yoksa kusur listesiyle kullanıcıya gösterilir
 *   REJECTED   QA geçmedi
 *   FAILED     sağlayıcı hatası
 */
export const ProductionTakeStatus = {
  QUEUED: 'QUEUED',
  SUBMITTED: 'SUBMITTED',
  GENERATED: 'GENERATED',
  PASSED: 'PASSED',
  WEAK: 'WEAK',
  REJECTED: 'REJECTED',
  FAILED: 'FAILED',
  CANCELED: 'CANCELED',
} as const;
export type ProductionTakeStatus = (typeof ProductionTakeStatus)[keyof typeof ProductionTakeStatus];
export const productionTakeStatusSchema = z.enum(ProductionTakeStatus);

/// Take neden üretildi. Prisma'da enum değil dize (`CreativeJob.provider`
/// deseni) — yeni bir kurtarma yolu migration istemesin; doğrulama burada.
/// `finish` (Dalga 4): seçilen 480p TASLAK take'in 1080p tamamlaması
/// (`draft/complete`) — API onu `initial` diye gösteriyordu.
export const PRODUCTION_TAKE_REASONS = [
  'initial',
  'reroll',
  'repair',
  'composite',
  'fallback',
  'user',
  'finish',
] as const;
export type ProductionTakeReason = (typeof PRODUCTION_TAKE_REASONS)[number];
export const productionTakeReasonSchema = z.enum(PRODUCTION_TAKE_REASONS);

/// Ara ürünün hâli — anahtar kare, müzik gibi sağlayıcı üretimleri de
/// gönderimde açılır (tutamaç yazılır), sonuçta `READY` olur.
export const ProductionAssetStatus = { PENDING: 'PENDING', READY: 'READY', FAILED: 'FAILED' } as const;
export type ProductionAssetStatus = (typeof ProductionAssetStatus)[keyof typeof ProductionAssetStatus];
export const productionAssetStatusSchema = z.enum(ProductionAssetStatus);

/**
 * Ara ürün türleri (plan §2.6). Nihai çıktılar burada DEĞİL — onlar
 * `Creative` satırı (`productionId` + `rendition`), kütüphane/yayın/onay
 * yolundan aynen geçsin diye. `RENDITION` varlığı kurgunun ham çıktısı;
 * teslim edilen dosya onun `Creative` kopyası.
 */
export const ProductionAssetKind = {
  PRODUCT_CUTOUT: 'PRODUCT_CUTOUT',
  ANGLE_VIEW: 'ANGLE_VIEW',
  KEYFRAME: 'KEYFRAME',
  OPTION_PREVIEW: 'OPTION_PREVIEW',
  MUSIC: 'MUSIC',
  SFX: 'SFX',
  VOICEOVER: 'VOICEOVER',
  MIX: 'MIX',
  ENDCARD: 'ENDCARD',
  EDIT_MASTER: 'EDIT_MASTER',
  RENDITION: 'RENDITION',
  CONTACT_SHEET: 'CONTACT_SHEET',
  COVER: 'COVER',
  SUBTITLES: 'SUBTITLES',
} as const;
export type ProductionAssetKind = (typeof ProductionAssetKind)[keyof typeof ProductionAssetKind];
export const productionAssetKindSchema = z.enum(ProductionAssetKind);

// ═══════════════════════════════════════════════════════════════════════════
// Kimlikler, anahtarlar, motor bayrağı
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 MOTOR CANLI (3 Eki 2026, kullanıcının açık talimatı: "her şeyi canlıya al").
 * Panel ve sohbetten başlatılan prodüksiyon GERÇEK fal çağrısı yapar ve
 * gerçek maliyetle tahsil edilir. Kapatmak: `false` → shared build →
 * `pm2 restart mentorivo-api mentorivo-worker mentorivo-web`. Kapalıyken
 * `POST /creatives/productions` (ve sohbet aracı) 503
 * `PRODUCTION_ENGINE_NOT_READY` döner — kredi rezerve edilmez, satır açılmaz.
 * fal bakiyesi biterse (403) worker işi durdurup tüm rezervi iade eder.
 */
export const PRODUCTION_ENGINE_LIVE = true;

/**
 * PROVA (Faz 10a, 2 Eki 2026) — motorun CANLI süreçlerde uçtan uca, fal'a
 * TEK KURUŞ ödemeden koşturulması. Prova prodüksiyonunda (`CreativeProduction
 * .rehearsal`) worker'ın her fal çağrısı taşıma katmanında prova taklidine
 * gider (yerel yedek medya, `costUsd` 0), QA yargıçları "geçti (prova)" der;
 * planlama LLM'i, para yolu (rezerv → kapanış → TAM iade), bildirim ve canlı
 * olaylar GERÇEK. Teslimler gerçek `Creative` satırı, `inputParams.rehearsal`.
 *
 * 🔴 Kim açabilir: YALNIZ bu listedeki çalışma alanları VE platform yöneticisi
 * (backstage'in kapısı — `isPlatformAdmin` + askıda olmayan panel erişimi).
 * Herkese açık istek şeması alanı REDDEDER (`productionStartSchema.rehearsal`);
 * prova yalnız `POST /creatives/productions/rehearsal` ucundan açılır ve
 * `PRODUCTION_ENGINE_LIVE = false` iken de çalışır (motor kapısı provaya
 * uygulanmaz — amaç tam olarak motoru açmadan önce koşturmak).
 */
export const PRODUCTION_REHEARSAL_WORKSPACES: readonly string[] = [
  /// Demo çalışma alanı (`demo`) — gerçek müşteri verisi yok.
  '019feabf-f145-7137-a55f-b81816aa8752',
];

export function productionRehearsalAllowed(workspaceId: string): boolean {
  return PRODUCTION_REHEARSAL_WORKSPACES.includes(workspaceId);
}

/**
 * Kredi defterindeki referans türü — rezervasyonlar bu türle açılır
 * (`referenceId` = prodüksiyon kimliği). İki kredi ikizi (API
 * `credit.service.ts`, worker `render-credit.ts`) bu dizeyi tanıyor ve
 * kesinleşen tutarı `CreativeProduction.creditsCharged`e AYNI İŞLEMDE
 * yazıyor. `PRODUCT_VISION_REF` ile aynı desen.
 */
export const PRODUCTION_CREDIT_REF = 'creative_production';

/**
 * Rezervasyon idempotency anahtarı — iki aşamalı tahsilat (plan §2.6):
 *   `production:<id>:storyboard` başlarken, storyboard evresi tahmini
 *   `production:<id>:shoot`      onayda, üretimin EN KÖTÜ durumu
 *   `production:<id>:rev:<n>`    onaydan sonra maliyetli n. revizyon
 *   `production:<id>:plan:<n>`   taslaktan önce AI zarfını aşan n. biçim rızası
 * Anahtar `reservationIdFor` ile defter satırının kimliğine dönüşüyor; aynı
 * anahtar iki kez rezerve edilemez.
 *
 * `attempt` — onayın/rızanın DENEME eki (`v<sürüm>`, gerekirse `v<sürüm>.<kısa>`):
 *   `production:<id>:shoot:v7`, `production:<id>:rev:1:v9`
 * 🔴 Neden (denetim bulgusu, 1 Eki 2026): `CreditService.reserve` aynı anahtarla
 * İADE EDİLMİŞ satırı da olduğu gibi döndürüyor (`state: 'refunded'`, yeni
 * kilit yok). Sabit `…:shoot` anahtarında yarışı kaybeden bir onayın iade
 * ettiği rezerv, bir sonraki onaya geri veriliyor ve üretim krediye hiç
 * kilitlenmeden koşuyordu — teslimde rezerv "kapanmış" bulunup 0 kesiliyordu.
 * Ek her yeni sürümde yeni bir satır açar; AYNI sürümdeki iki eşzamanlı onay
 * yine aynı satırı paylaşır (çift kilit yok). Kapanış anahtarı değil kolonu
 * (`productionReservationId`, revizyonda `reservationId`) okuyor.
 */
export type ProductionReservationPhase =
  | 'storyboard'
  | 'shoot'
  | { revision: number }
  /// Onaydan sonra kullanıcının açtığı yeniden çekim turu — `production:<id>:retry:<shotId>:<n>`
  /// (`productionRetryConsentSchema` başlığı: yeni tur YENİ rıza).
  | { retry: { shotId: string; n: number } }
  /**
   * Taslaktan ÖNCE kullanıcının AI zarfını aşan biçim seçimi (Dalga 5) —
   * `production:<id>:plan:<n>`; storyboard rezerviyle birlikte gerçek maliyetle
   * kapanır (`productionPlanConsentSchema` başlığı).
   */
  | { plan: number };
const RESERVATION_ATTEMPT = /^[A-Za-z0-9._-]{1,48}$/;
/// Çekim kimliği anahtarın bir parçası — `:` ayırıcıyı bozmasın.
const RESERVATION_SHOT = /^[A-Za-z0-9-]{1,64}$/;
export function productionReservationKey(
  productionId: string,
  phase: ProductionReservationPhase,
  attempt?: string,
): string {
  if (attempt !== undefined && !RESERVATION_ATTEMPT.test(attempt)) {
    throw new Error(`Rezerv deneme eki geçersiz: ${attempt}`);
  }
  const suffix = attempt === undefined ? '' : `:${attempt}`;
  if (typeof phase === 'string') return `production:${productionId}:${phase}${suffix}`;
  if ('retry' in phase) {
    const { shotId, n } = phase.retry;
    if (!RESERVATION_SHOT.test(shotId)) {
      throw new Error(`Yeniden çekim rezervi: çekim kimliği geçersiz: ${shotId}`);
    }
    if (!Number.isInteger(n) || n < 1) {
      throw new Error(`Yeniden çekim numarası 1'den başlayan tam sayı olmalı: ${n}`);
    }
    return `production:${productionId}:retry:${shotId}:${n}${suffix}`;
  }
  if ('plan' in phase) {
    if (!Number.isInteger(phase.plan) || phase.plan < 1) {
      throw new Error(`Biçim rızası numarası 1'den başlayan tam sayı olmalı: ${phase.plan}`);
    }
    return `production:${productionId}:plan:${phase.plan}${suffix}`;
  }
  if (!Number.isInteger(phase.revision) || phase.revision < 1) {
    throw new Error(`Revizyon numarası 1'den başlayan tam sayı olmalı: ${phase.revision}`);
  }
  return `production:${productionId}:rev:${phase.revision}${suffix}`;
}

/**
 * KAPI BOŞTA SÜRESİ — kullanıcı kararı (1 Eki 2026); sabit burada ki tek yerden
 * değişebilsin (worker süpürücüsü okur, arayüz "N gün içinde karar verilmezse"
 * cümlesini buradan kurar).
 *
 * Kapıda (`AWAITING_*`) bekleyen prodüksiyon bir rezervi kilitli tutuyor —
 * denetim bulgusu: soruları hiç cevaplanmayan bir ASK prodüksiyonunun storyboard
 * kredisi sonsuza dek kilitliydi. Kural:
 *   REMIND  gün  `production.question` hatırlatması (kapı + sürüm başına BİR kez)
 *   CANCEL  gün  kapının süresi doldu:
 *                · rıza kapısı (`REVISION_CONFIRM`) → revizyon REDDEDİLİR (`expired`)
 *                  ve film kaldığı yerden sürer. İptal etmek, parası ödenmiş
 *                  take'leri kullanıcıya kesip ona hiçbir şey vermemek olurdu;
 *                  rıza kapısının kendi rezervi yok (rezerv rızada açılıyor).
 *                · diğer kapılar → İPTAL (`ProductionErrorCode.GATE_EXPIRED`): o ana
 *                  kadarki GERÇEK maliyet kesilir, kalanı iade, `production.failed`
 * Boşta süresi satırın SON yazımından (`updatedAt`) ölçülür: kapıdaki her
 * kullanıcı eylemi (kısmi cevap, sıra değişikliği) saati sıfırlar ve sürümü
 * artırdığı için hatırlatma da yeniden hak edilir.
 */
export const PRODUCTION_GATE_IDLE_REMIND_DAYS = 7;
export const PRODUCTION_GATE_IDLE_CANCEL_DAYS = 14;

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SAĞLAYICI BEKLEMESİ — "üretimini engelleme, iptal olmasın" (kullanıcı
 * kararı, 3 Eki 2026)
 * ═══════════════════════════════════════════════════════════════════════════
 * İlk gerçek prodüksiyon çekimin ortasında fal bakiyemiz bitince (403 "User is
 * locked") FAILED oldu ve 1904 kredi iade edildi — kullanıcının hiçbir suçu
 * yokken filmi öldü. BİZİM sağlayıcı hesabımızın arızası (bakiye bitti, anahtar
 * reddedildi; worker `isProviderAccountFault`) artık prodüksiyonu düşürmüyor:
 *   · prodüksiyon olduğu durum/aşamada kalır, bu kayıt `active` olur ve durum
 *     makinesi hiçbir eylem üretmez (kapı gibi, ama kullanıcıyı beklemiyor)
 *   · rezerv AÇIK kalır (hiçbir şey kesilmez/iade edilmez), biten take'ler ve
 *     ara ürünler korunur; uçuştaki istekler take hakkı YAKILMADAN bekler —
 *     koşan/bitmiş isteğin tutamacı KORUNUR (fal iptali koşan işi durdurmuyor
 *     ve faturalıyor; sonuç bekleme bitince ücretsiz okunur), yalnız kuyrukta
 *     bekleyen ya da hiç koşmamış istek iptal edilip yeniden sıraya girer
 *   · worker süpürücüsünün dürtmesiyle sağlayıcıyı yoklar (fal'da ücretsiz
 *     bakiye okuması), açılınca kaldığı aşamadan sürer
 *   · `PRODUCTION_PROVIDER_HOLD_MAX_DAYS` günü aşarsa eskisi gibi FAILED + TAM
 *     iade — kapı boşta kuralının (7 gün hatırlatma / 14 gün iptal) ilk eşiği:
 *     bir haftadır üretemiyorsak kullanıcının kredisini daha fazla kilitlemeyiz.
 * Kullanıcıya tek sakin haber (`production.provider_wait`), platform
 * yöneticilerine "bakiye yükle" uyarısı (`production.provider_blocked`).
 * Bekleme sırasında kullanıcı iptal ederse TAM iade (bizim arızamız — worker
 * `settleCanceled`); tavanın ve yeniden kilitlenme sınırının kodu
 * `PRODUCTION_PROVIDER_UNAVAILABLE`.
 */
export const PRODUCTION_PROVIDER_HOLD_MAX_DAYS = 7;

/**
 * Yoklama "açık" dediği hâlde aynı beklemenin kaç kez YENİDEN kilitlenebileceği
 * (fal bakiye kilidi HARİÇ — onun yoklaması gerçek: bakiye). fal'ın bakiye
 * dışı 401/403'ü (uç/izin reddi) ve LLM anahtarı için ücretsiz bir yoklama yok;
 * yoklama "dene" diyor, aynı ret geliyor — sınırsız bırakılırsa 7 gün boyunca
 * her dürtmede serbest bırak → yeniden çalıştır → kilitlen döngüsü dönüyordu
 * (denetim bulgusu, 3 Eki 2026). Sınır dolunca FAILED + TAM iade.
 */
export const PRODUCTION_PROVIDER_HOLD_MAX_RELOCKS = 6;

export const productionProviderHoldSchema = z.object({
  /// Beklemenin BAŞLANGICI (ISO) — yoklama turlarında ve yeniden kilitlenmede
  /// korunur; 7 günlük tavan buradan sayılır.
  since: z.string().min(1),
  /// Sağlayıcının hata anahtarı (`creative.unavailable.provider_balance_exhausted`…).
  reasonKey: z.string().min(1).max(120),
  /// Yoklama yolu: `fal` → ücretsiz bakiye okuması; `other` → aşamayı yeniden dene.
  provider: z.enum(['fal', 'other']),
  /// Arızanın geldiği aşama (teşhis).
  stage: z.string().min(1).max(40),
  /// `true`: motor bekliyor. `false`: sağlayıcı açıldı, iş yeniden koşuyor —
  /// kayıt TARİHÇE olarak kalır; kısa süre içinde yeniden kilitlenirse aynı
  /// bekleme sürer (başlangıç, bildirim tekrar etmez).
  active: z.boolean(),
  /// Yapılan yoklama sayısı — geri çekilme (5 → 10 → 20 → 30 dk) buna bağlı.
  /// Yeniden kilitlenme de bir sayılır (açıldı sandık, açılmadı).
  probes: z.number().int().min(0),
  /// Serbest bırakıldıktan sonra AYNI beklemede kaç kez yeniden kilitlendi
  /// (`PRODUCTION_PROVIDER_HOLD_MAX_RELOCKS`). Eski kayıtta yok = 0.
  relocks: z.number().int().min(0).optional(),
  /// Sıradaki yoklamanın zamanı (ISO).
  nextProbeAt: z.string().min(1),
  /// Sağlayıcı açılıp iş yeniden koştuğu an (ISO); bekleme sürerken yok.
  releasedAt: z.string().min(1).optional(),
  /// Haberler (kullanıcı + platform yöneticileri) gitti mi. `false` = henüz
  /// değil (kurtarma yolu açar; worker ilk yoklamada gönderir). Yok = gitti.
  announced: z.boolean().optional(),
  /// Kurtarma yoluyla (FAILED → bekleme) açıldıysa: rıza ve yeniden ayrılan kredi.
  rescued: z
    .object({
      at: z.string().min(1),
      consentedCredits: z.number().int().min(0),
      priceMaxCredits: z.number().int().min(0),
      reservedCredits: z.number().int().min(0),
    })
    .optional(),
});
export type ProductionProviderHold = z.infer<typeof productionProviderHoldSchema>;

/// fal'ın bakiye kilidi (403 "User is locked") — yoklaması GERÇEK (bakiye okuması).
export const PRODUCTION_FAL_BALANCE_REASON_KEY = 'creative.unavailable.provider_balance_exhausted';

/**
 * fal HESABININ hata anahtarları (worker `fal-queue.ts`): 403 bakiye bitti ve
 * 401/403 anahtar reddi. Bunlarda yoklama ÜCRETSİZ bakiye okuması; diğer
 * sağlayıcılarda (LLM anahtarı) aşama yeniden denenir. ⚠️ Bakiye DIŞI 403
 * (`image_provider_missing`) bakiyeyle açılmayabilir — yeniden kilitlenme
 * sınırı (`PRODUCTION_PROVIDER_HOLD_MAX_RELOCKS`) onu bitiriyor.
 */
export const PRODUCTION_FAL_ACCOUNT_REASON_KEYS: readonly string[] = [
  PRODUCTION_FAL_BALANCE_REASON_KEY,
  'creative.unavailable.image_provider_missing',
];

export function productionProviderOfReason(reasonKey: string): ProductionProviderHold['provider'] {
  return PRODUCTION_FAL_ACCOUNT_REASON_KEYS.includes(reasonKey) ? 'fal' : 'other';
}

/** Kolonun ham JSON'u → kayıt; bozuk/boş satır `null` (fırlatmaz). */
export function parseProductionProviderHold(raw: unknown): ProductionProviderHold | null {
  if (raw === null || raw === undefined) return null;
  const parsed = productionProviderHoldSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/**
 * Ekrana çıkan bekleme — yalnız terminal OLMAYAN ve beklemesi `active` satırda.
 * Ham sağlayıcı metni ve anahtar ÇIKMAZ: ekran kullanıcının bakiyesini değil
 * bizim kapasitemizi anlatıyor; yalnız başlangıç.
 */
export function productionProviderWaitOf(status: string, raw: unknown): { since: string } | null {
  if ((PRODUCTION_TERMINAL_STATUSES as readonly string[]).includes(status)) return null;
  const hold = parseProductionProviderHold(raw);
  return hold?.active ? { since: hold.since } : null;
}

/// Canlı katman (`LIVE_TOPICS` → `productions`) olay referansı.
export function productionLiveRef(productionId: string): string {
  return `production:${productionId}`;
}
/** `productionLiveRef`in tersi — tanınmayan biçimde `null`. */
export function parseProductionLiveRef(ref: string | null | undefined): string | null {
  if (!ref?.startsWith('production:')) return null;
  const id = ref.slice('production:'.length);
  return id.length > 0 && !id.includes(':') ? id : null;
}

/**
 * Deterministik BullMQ iş kimliği: `<id>.<iş>.<hedef>.v<version>`.
 *
 * BullMQ özel kimlikte `:` İSTEMİYOR (Redis anahtar ayırıcısı) — bu yüzden
 * nokta. Aynı iş iki kez kuyruğa atılırsa ikincisi kendiliğinden düşer;
 * `version` kullanıcı düzenlemesinde arttığı için bayat aşama sonucu yeni
 * kimlikle çakışmaz.
 */
export function productionJobId(
  productionId: string,
  job: string,
  target: string,
  version: number,
): string {
  const clean = (part: string) => part.replace(/[:\s]/g, '_');
  return `${clean(productionId)}.${clean(job)}.${clean(target)}.v${version}`;
}

/// R2 öneki: `ws/<ws>/productions/<id>/` — kiracı silinince `ws/<id>/`
/// temizliği prodüksiyon dosyalarını da kapsar.
export function productionStoragePrefix(workspaceId: string, productionId: string): string {
  return `ws/${workspaceId}/productions/${productionId}/`;
}

/**
 * Teslim (`Creative.rendition`) anahtarı.
 *   `master`        seçilen boyutta tam sürüm — geçmiş YALNIZ bunu listeler
 *   `4x5`           ek boyut (güvenli alana göre yeniden kadraj)
 *   `9x16.cut15`    15 sn'lik kesim
 *   `9x16.sub`      altyazılı sürüm (`.cut15.sub` birlikte de olabilir)
 */
export const PRODUCTION_MASTER_RENDITION = 'master';

/**
 * KURGU (teslim turu) — teslimden SONRA istenen revizyon (plan §2.2: "müziği
 * değiştir", "sonu daha güçlü" kullanıcı sonucu GÖRDÜKTEN sonra söyleniyor)
 * prodüksiyonu YENİ BİR KURGUYLA yeniden açar; önceki teslimler (`Creative`
 * satırları) DOKUNULMADAN kalır — yayındaki bir reklam altındaki dosyanın
 * değiştiğini görmemeli. Yeni kurgunun teslimleri aynı adın sonuna `.r<kurgu>`
 * eki alır (`master.r1`, `4x5.r1`, `9x16.cut15.sub.r1`), böylece teslim
 * yazıcısının `(productionId, rendition)` güncellemesi eski satıra hiç dokunmaz.
 *
 * Kurgu numarası SAKLANMIYOR, TÜRETİLİYOR (`productionCutIndex`): uygulanmış
 * (`applied`) teslim-sonrası (`postDelivery`) revizyon sayısı — ses/referans
 * "dönemi" (`assetEpoch`, worker) ile aynı desen. 0 = ilk teslim.
 *
 * Geçmiş ekranı her prodüksiyondan TEK kart gösterir: EN SON teslim edilen
 * kurgunun master'ı (`CreativeProduction.masterCreativeId` — teslim yazıcısı
 * master'ı yazdığı işlemde günceller).
 */
export const PRODUCTION_CUT_SUFFIX = 'r';
const CUT_PART = new RegExp(`\\.${PRODUCTION_CUT_SUFFIX}(\\d{1,3})$`);

export function productionCutIndex(
  revisions: readonly { status: string; postDelivery?: boolean }[],
): number {
  return revisions.filter((rev) => rev.status === 'applied' && rev.postDelivery === true).length;
}

/**
 * VARYANT EKİ (Dalga 4, 2 Eki 2026) — bir prodüksiyonun birden çok reklamı
 * (`deliverablePlanSchema`: açılış varyantları ya da farklı fikirler) aynı
 * kurguda yan yana teslim ediliyor; ad `<boyut/master>[.cut<sn>][.sub][.v<n>][.r<kurgu>]`:
 * `master.v2`, `4x5.v2`, `9x16.cut6.v2`, `master.v2.r1`. BİRİNCİL varyant (`v1`)
 * ek ALMAZ — tek reklamlı prodüksiyonun (ve göç öncesi her teslimin) adı
 * değişmiyor. Kurgu eki her zaman EN SONDA (`CUT_PART` onu sondan okuyor).
 */
const VARIANT_PART = /(?:^|\.)(v[1-9])(?:\.r\d{1,3})?$/;

function variantSuffix(variant: string | null | undefined): string | null {
  return variant && variant !== PRODUCTION_PRIMARY_VARIANT ? variant : null;
}

/// Kurgunun (ve varyantın) master teslim adı: `master`, `master.r1`, `master.v2`, `master.v2.r1`.
export function productionMasterRendition(
  cut: number,
  variant: ProductionVariantKey | null = null,
): string {
  const parts = [PRODUCTION_MASTER_RENDITION];
  const suffix = variantSuffix(variant);
  if (suffix) parts.push(suffix);
  if (cut > 0) parts.push(`${PRODUCTION_CUT_SUFFIX}${cut}`);
  return parts.join('.');
}

/// Teslim adından kurgu numarası (eki yoksa 0).
export function productionRenditionCut(rendition: string | null | undefined): number {
  const match = rendition ? CUT_PART.exec(rendition) : null;
  return match ? Number(match[1]) : 0;
}

/// Teslim adından varyant (eki yoksa birincil `v1`).
export function productionRenditionVariant(
  rendition: string | null | undefined,
): ProductionVariantKey {
  const match = rendition ? VARIANT_PART.exec(rendition) : null;
  const key = match?.[1];
  return (PRODUCTION_VARIANT_KEYS as readonly string[]).includes(key ?? '')
    ? (key as ProductionVariantKey)
    : PRODUCTION_PRIMARY_VARIANT;
}

/// Teslim adı bir kurgunun (herhangi bir varyantın) master'ı mı.
export function isProductionMasterRendition(rendition: string | null | undefined): boolean {
  if (rendition === null || rendition === undefined) return false;
  return (
    rendition ===
    productionMasterRendition(
      productionRenditionCut(rendition),
      productionRenditionVariant(rendition),
    )
  );
}

export function productionRendition(input: {
  aspect: ProductionAspect;
  /// Ana boyutun tam sürümü mü — `true` ise yalnız `master`.
  master?: boolean;
  cutSeconds?: number | null;
  subtitles?: boolean;
  /// Kurgu numarası (`productionCutIndex`) — 0/yok = ilk teslim, ek yok.
  cut?: number;
  /// Varyant (`deliverablePlanSchema`) — `v1`/yok = ek yok.
  variant?: ProductionVariantKey | null;
}): string {
  const parts = [input.master && !input.cutSeconds ? PRODUCTION_MASTER_RENDITION : aspectSlug(input.aspect)];
  if (input.cutSeconds) parts.push(`cut${input.cutSeconds}`);
  if (input.subtitles) parts.push('sub');
  const suffix = variantSuffix(input.variant);
  if (suffix) parts.push(suffix);
  if (input.cut && input.cut > 0) parts.push(`${PRODUCTION_CUT_SUFFIX}${input.cut}`);
  return parts.join('.');
}

/**
 * Bildirim tekilleştirme anahtarı — aynı kapı için iki bildirim gitmesin
 * (aşama işi yeniden denenirse). `version` dahil: kullanıcı düzenleyip kapı
 * yeniden açılırsa yeni bildirim meşru.
 */
export function productionNotifyDedupeKey(productionId: string, type: string, version: number): string {
  return `production:${productionId}:${type}:v${version}`;
}

/**
 * Hata kodları — ekrana çıkan i18n anahtarının son parçası; ham sağlayıcı
 * metni API yanıtına ÇIKMAZ (`errorCode` kolonu bunu taşır, `error` teşhis).
 */
export const ProductionErrorCode = {
  ENGINE_NOT_READY: 'PRODUCTION_ENGINE_NOT_READY',
  NOT_FOUND: 'PRODUCTION_NOT_FOUND',
  /// İstemcinin `version`u bayat — başkası (ya da motor) bu arada değiştirdi.
  STALE_VERSION: 'PRODUCTION_STALE_VERSION',
  /// İstek açık kapıyla uyuşmuyor (ör. soru yokken cevap).
  WRONG_GATE: 'PRODUCTION_WRONG_GATE',
  /// Rıza verilen kredi güncel tahminden düşük — fiyat değişti, yeniden göster.
  PRICE_CHANGED: 'PRODUCTION_PRICE_CHANGED',
  ASPECT_NOT_ALLOWED: 'PRODUCTION_ASPECT_NOT_ALLOWED',
  PRODUCT_SOURCE_INVALID: 'PRODUCTION_PRODUCT_SOURCE_INVALID',
  TERMINAL: 'PRODUCTION_TERMINAL',
  /// Aşama başarısız ve kurtarılamadı — `production.failed`, rezerv TAMAMEN iade.
  STAGE_FAILED: 'PRODUCTION_STAGE_FAILED',
  /// Bütün çekim rotaları düştü (kapı `SHOT_FAILED`; çekim satırının kodu).
  SHOT_FAILED: 'PRODUCTION_SHOT_FAILED',
  /// Aşama işleyicisi henüz yazılmadı (worker `StageNotImplementedError`) —
  /// prodüksiyon FAILED, rezerv TAMAMEN iade.
  STAGE_NOT_IMPLEMENTED: 'PRODUCTION_STAGE_NOT_IMPLEMENTED',
  /// TEK take'in sağlayıcı/ölçüm/QA hatası — yalnız take FAILED (take satırının
  /// kodu), makine yeniden çekime gider; prodüksiyon sürer.
  TAKE_FAILED: 'PRODUCTION_TAKE_FAILED',
  /// Çekimin anahtar karesi tur tavanında QA'dan geçmedi — kapı `SHOT_FAILED`,
  /// çekim satırının kodu (take'leri tükenen çekimden ayırt edilsin: ona yeniden
  /// çekim değil düzenleme/çıkarma çare).
  KEYFRAME_FAILED: 'PRODUCTION_KEYFRAME_FAILED',
  /// Kapı `PRODUCTION_GATE_IDLE_CANCEL_DAYS` gün kullanıcıyı bekledi — iptal
  /// (gerçek maliyet kesilir, kalanı iade), `production.failed`.
  GATE_EXPIRED: 'PRODUCTION_GATE_EXPIRED',
  /// Politika ön denetimi konsepti/senaryoyu uyumlu bir sürüme getiremedi
  /// (ör. ürünün kendisi yasaklı kategori) — terminal, rezerv TAMAMEN iade.
  /// Worker'da `StagePolicyBlockedError`; revizyonda ise yalnız revizyon reddedilir.
  POLICY_BLOCKED: 'PRODUCTION_POLICY_BLOCKED',
  /// Sağlayıcı beklemesi bitmedi: bizim sağlayıcı hesabımız
  /// `PRODUCTION_PROVIDER_HOLD_MAX_DAYS` gün kullanılamadı ya da yoklama açık
  /// dediği hâlde ret sürdü — terminal, rezerv TAMAMEN iade. Kullanıcının suçu
  /// değil; ekran bunu "kapasitemiz açılmadı" diye anlatır, genel hata değil.
  PROVIDER_UNAVAILABLE: 'PRODUCTION_PROVIDER_UNAVAILABLE',
} as const;
export type ProductionErrorCode = (typeof ProductionErrorCode)[keyof typeof ProductionErrorCode];

// ═══════════════════════════════════════════════════════════════════════════
// Başlatma isteği
// ═══════════════════════════════════════════════════════════════════════════

/// LLM'in ürettiği kısa kimlikler (konsept, soru, şık, beat) — URL'de ve
/// sohbet kartında taşınabilir olsun.
const shortIdSchema = z
  .string()
  .min(1)
  .max(40)
  .regex(/^[A-Za-z0-9_-]+$/);

const assetKeySchema = z.string().min(1).max(400);

/**
 * Ürün kaynağı — TAM OLARAK BİRİ: katalog ürünü (`productId`; görselleri
 * mağazadan) ya da yüklenen fotoğraflar (`assetKeys`, 1–6). Kimlik kaynağı
 * YALNIZ müşterinin orijinal yüklemesi (REPORT §2.1/7) — üretilmiş bir görsel
 * referans olmaz.
 */
export const productionProductSchema = z
  .object({
    productId: z.uuid().optional(),
    assetKeys: z.array(assetKeySchema).min(1).max(PRODUCTION_MAX_PHOTOS).optional(),
  })
  .refine((value) => Boolean(value.productId) !== Boolean(value.assetKeys?.length), {
    message: 'productId ya da assetKeys — tam olarak biri gerekli.',
    path: ['assetKeys'],
  });
export type ProductionProductSource = z.infer<typeof productionProductSchema>;

export const productionVoiceoverSchema = z.object({
  enabled: z.boolean().default(false),
  /// Ses kimliği (ElevenLabs ses adı). Boşsa direktör seçer.
  voice: z.string().trim().min(1).max(64).optional(),
});

/// Son kart — tipografi gerçek fontla deterministik basılır, model çizmez
/// (REPORT §4). Fiyat mağazadan gelir, buraya yazılmaz.
export const productionEndCardSchema = z.object({
  cta: z.string().trim().min(1).max(40).optional(),
  slogan: z.string().trim().min(1).max(80).optional(),
  showPrice: z.boolean().optional(),
  /// `BrandLogo.id`.
  logoId: z.uuid().optional(),
});

export const productionStartSchema = z
  .object({
    product: productionProductSchema,
    note: z.string().trim().max(PRODUCTION_NOTE_MAX).optional(),
    /// 🔴 ZORUNLU ve İLK soru (kullanıcı kararı) — varsayılanı yok.
    aspect: productionAspectSchema,
    placement: productionPlacementSchema.optional(),
    /// `null` = AI yerleşime göre seçer (rezerv tahmini `PLACEMENT_SPECS.defaultDuration`la kurulur).
    durationSeconds: productionDurationSchema.nullable().default(null),
    mode: productionModeSchema.default('DECIDE'),
    /// Dış ses, üst yazı ve son kart dili. Reklam metni dili bilerek tr/en
    /// (proje kararı — `copyLanguage` ile aynı).
    language: z.enum(['tr', 'en']).default('tr'),
    voiceover: productionVoiceoverSchema.default({ enabled: false }),
    music: z.boolean().default(true),
    /// Ek boyutlar — ana boyuttan FARKLI, güvenli alana göre yeniden kadraj.
    extraAspects: z.array(productionAspectSchema).max(PRODUCTION_ASPECTS.length - 1).default([]),
    /// Kısa kesimler (sn) — ana süreden KISA olmalı.
    cutdowns: z.array(productionDurationSchema).max(3).default([]),
    subtitles: z.boolean().default(false),
    endCard: productionEndCardSchema.optional(),
    /// Reklamın hedefi — çıktı biçimi ve fikir kararının girdisi; yoksa AI çıkarır.
    goal: productionGoalSchema.optional(),
    /**
     * YORULAN bir reklamın YENİLENMESİ (Faz 8b): kaynak reklam (`Ad.id`). Varsa
     * strateji adımı bunu bilir (yeni açılışlar / yeni fikir) ve performans
     * verisini okuyabilir. API çalışma alanında var olduğunu doğrular.
     */
    sourceAdId: z.uuid().optional(),
    /**
     * Prodüksiyonun TÜRÜ (Dalga 5) — yoksa reklam videosu. `IMAGE_SET`te
     * `imageSet` zorunlu; videoya özgü alanlar (dış ses, altyazı, kesim, süre,
     * ek boyut) varsayılanında kalmak ZORUNDA, `music` yok sayılır. `aspect`
     * seri/karuselde her karenin boyutu, banner setinde ana görselin boyutu.
     */
    kind: productionKindSchema.default('VIDEO'),
    imageSet: imageSetRequestSchema.optional(),
    /**
     * 🔴 PROVA bayrağı bu şemadan GEÇEMEZ (Faz 10a): herkese açık başlatma
     * (panel, sohbet aracı, tahmin) alanı görürse 400 döner — sessizce atılan
     * bir alan "prova açtım" sanıp GERÇEK ücretli üretim başlatmak demekti.
     * Prova yalnız platform yöneticisinin ayrı ucundan
     * (`PRODUCTION_REHEARSAL_WORKSPACES` başlığı).
     */
    rehearsal: z
      .never({ error: 'Prova yalnız yönetici ucundan açılır.' })
      .optional(),
  })
  .superRefine((input, ctx) => {
    if (input.kind === 'IMAGE_SET') {
      imageSetStartIssues(input).forEach((issue) => ctx.addIssue({ code: 'custom', ...issue }));
    } else if (input.imageSet) {
      ctx.addIssue({
        code: 'custom',
        path: ['imageSet'],
        message: 'Set ayarı yalnız görsel kampanyada.',
      });
    }
    if (input.extraAspects.includes(input.aspect)) {
      ctx.addIssue({ code: 'custom', path: ['extraAspects'], message: 'Ek boyut ana boyutla aynı olamaz.' });
    }
    if (new Set(input.extraAspects).size !== input.extraAspects.length) {
      ctx.addIssue({ code: 'custom', path: ['extraAspects'], message: 'Ek boyutlar tekrar edemez.' });
    }
    if (!placementAllowsAspect(input.placement, input.aspect)) {
      /* Mesaj İNSAN cümlesi (alanın altına yazılıyor); kod `params.code`ta —
         `ZodValidationPipe` onu yanıtın `code`una taşıyor. Önceden kodun
         kendisi mesajdı: istemci dallanamıyor, form ham sabiti basıyordu. */
      ctx.addIssue({
        code: 'custom',
        path: ['aspect'],
        message: 'Bu yerleşim seçilen boyutu kabul etmiyor.',
        params: { code: ProductionErrorCode.ASPECT_NOT_ALLOWED },
      });
    }
    const placement = input.placement ? PLACEMENT_SPECS[input.placement] : null;
    if (placement && input.durationSeconds !== null && input.durationSeconds > placement.maxDurationSec) {
      ctx.addIssue({
        code: 'custom',
        path: ['durationSeconds'],
        message: `Bu yerleşim en fazla ${placement.maxDurationSec} sn kabul ediyor.`,
      });
    }
    if (new Set(input.cutdowns).size !== input.cutdowns.length) {
      ctx.addIssue({ code: 'custom', path: ['cutdowns'], message: 'Kesimler tekrar edemez.' });
    }
    /* Süre AI'a bırakıldıysa (`null`) kesimler PLANLAMA süresine göre sınanır
       (yerleşimin varsayılanı — storyboard rezervi de onunla kuruluyor,
       `planningDurationFor`). Önceden `null`da hiç sınanmıyordu: 10 sn'lik
       Stories'e 30 sn'lik "kesim" geçiyordu. Faz 3 sözleşmesi: AI'ın seçtiği
       süre en uzun kesimden UZUN olmalı. */
    const planned = input.durationSeconds ?? placement?.defaultDuration ?? PRODUCTION_FALLBACK_DURATION;
    if (input.cutdowns.some((cut) => cut >= planned)) {
      ctx.addIssue({
        code: 'custom',
        path: ['cutdowns'],
        message:
          input.durationSeconds === null
            ? `Kesim, planlanan süreden (${planned} sn) kısa olmalı; daha uzun kesim için süreyi seçin.`
            : 'Kesim ana süreden kısa olmalı.',
      });
    }
  });
export type ProductionStartInput = z.infer<typeof productionStartSchema>;
export type ProductionStartRequest = z.input<typeof productionStartSchema>;

/**
 * Görsel kampanya isteğinin kuralları (`productionStartSchema` superRefine'ı).
 * Videoya özgü alanlar VARSAYILANINDA olmalı: sessizce yok saymak, kullanıcının
 * "altyazılı 15 sn" isteğini fiyatlanmamış ve üretilmeyecek bir söz olarak
 * kabul etmek olurdu. `music` yok sayılır (varsayılanı `true`, görselde ses yok).
 */
function imageSetStartIssues(
  input: Pick<
    ProductionStartInput,
    | 'imageSet'
    | 'aspect'
    | 'placement'
    | 'voiceover'
    | 'subtitles'
    | 'cutdowns'
    | 'durationSeconds'
    | 'extraAspects'
  >,
): { path: string[]; message: string }[] {
  const issues: { path: string[]; message: string }[] = [];
  if (!input.imageSet) {
    issues.push({ path: ['imageSet'], message: 'Görsel kampanya için set türü gerekli.' });
    return issues;
  }
  if (input.voiceover.enabled) {
    issues.push({ path: ['voiceover'], message: 'Görsel kampanyada dış ses yok.' });
  }
  if (input.subtitles) {
    issues.push({ path: ['subtitles'], message: 'Görsel kampanyada altyazı yok.' });
  }
  if (input.cutdowns.length > 0) {
    issues.push({ path: ['cutdowns'], message: 'Görsel kampanyada kesim yok.' });
  }
  if (input.durationSeconds !== null) {
    issues.push({ path: ['durationSeconds'], message: 'Görsel kampanyada süre yok.' });
  }
  if (input.extraAspects.length > 0) {
    issues.push({
      path: ['extraAspects'],
      message: 'Görsel kampanyada ek boyut yok — birden çok boyut için banner seti seçin.',
    });
  }
  if (input.imageSet.setType === 'CAROUSEL' && !CAROUSEL_ASPECTS.includes(input.aspect)) {
    issues.push({
      path: ['aspect'],
      message: 'Karusel kartı kare (1:1) ya da dikey gönderi (4:5) olmalı.',
    });
  }
  if (input.placement && !IMAGE_SET_PLACEMENTS.includes(input.placement)) {
    issues.push({ path: ['placement'], message: 'Bu yerleşim görsel reklam almıyor.' });
  }
  return issues;
}

/// İsteğin görsel kampanya parçası — VIDEO'da (ya da eksikse) `null`.
export function imageSetOf(
  input: Pick<ProductionStartInput, 'kind' | 'imageSet'> | null | undefined,
): ImageSetRequest | null {
  return input?.kind === 'IMAGE_SET' && input.imageSet ? input.imageSet : null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Aşama çıktıları — DB'de JSON, şema burada
// ═══════════════════════════════════════════════════════════════════════════

/**
 * ÜRÜN DOSYASI (ANLA). Fotoğraf + mağaza açıklaması/fiyat/varyant/yorum +
 * marka. Bilinmeyen alan BOŞ kalır: uydurulmuş bir kullanım jesti
 * jestsizlikten kötü (hafıza dersi) ve kanıt noktası KAYNAĞIYLA yazılır.
 *
 * `internal` bloğu direktörün çalışma notu (klişe listesi, riskli iddialar) —
 * yanıta girmez (`ProductionDossierPublic`).
 */
export const dossierSchema = z.object({
  product: z.object({
    /// Ürünün İngilizce kısa tanımı — prompt derleyicisinin girdisi.
    subjectEn: z.string().trim().min(1).max(160),
    category: z.string().trim().max(80).nullable(),
    colors: z.array(z.string().trim().min(1).max(40)).max(8),
    materials: z.array(z.string().trim().min(1).max(40)).max(8),
    shape: z.string().trim().max(160).nullable(),
    /// Etiket satırları HARFİ HARFİNE — QA'nın karşılaştırma referansı.
    labelText: z.array(z.string().trim().min(1).max(160)).max(20),
    /// Etiketi yoğun ürün mü — rota kararı (Gemini yalnız yazısız üründe).
    hasText: z.boolean(),
    distinctiveDetails: z.array(z.string().trim().min(1).max(160)).max(8),
  }),
  usage: z.object({
    gesture: z.string().trim().max(200).nullable(),
    steps: z.array(z.string().trim().min(1).max(160)).max(8),
    who: z.string().trim().max(120).nullable(),
    where: z.string().trim().max(120).nullable(),
  }),
  offer: z.object({
    price: z.number().nonnegative().nullable(),
    currency: z.string().trim().length(3).nullable(),
    discountPct: z.number().min(0).max(100).nullable(),
  }),
  proofPoints: z
    .array(
      z.object({
        claim: z.string().trim().min(1).max(200),
        source: z.enum(['description', 'review', 'store', 'user', 'photo']),
        quote: z.string().trim().max(300).nullable(),
      }),
    )
    .max(8),
  audience: z.string().trim().max(240).nullable(),
  problem: z.string().trim().max(240).nullable(),
  emotion: z.string().trim().max(120).nullable(),
  brand: z.object({
    name: z.string().trim().max(80).nullable(),
    tone: z.string().trim().max(160).nullable(),
    colors: z.array(z.string().trim().min(1).max(40)).max(6),
    font: z.string().trim().max(80).nullable(),
    rules: z.array(z.string().trim().min(1).max(200)).max(10),
    memoryNotes: z.array(z.string().trim().min(1).max(300)).max(10),
  }),
  internal: z.object({
    categoryCliches: z.array(z.string().trim().min(1).max(160)).max(12),
    riskyClaims: z.array(z.string().trim().min(1).max(200)).max(10),
  }),
});
export type ProductionDossier = z.infer<typeof dossierSchema>;
export type ProductionDossierPublic = Omit<ProductionDossier, 'internal'>;
export function publicDossier(dossier: ProductionDossier): ProductionDossierPublic {
  const { internal: _internal, ...rest } = dossier;
  return rest;
}

/// Konsept türleri birbirinden AYRI olmalı (plan §2.1) — aynı türden iki
/// konsept, 26 analizde ölçülen "her ürüne aynı üç kalıp" arızasının kendisi.
export const PRODUCTION_CONCEPT_GENRES = [
  'drama',
  'comedy',
  'surreal',
  'documentary',
  'asmr',
  'metaphor',
  'mini_film',
  'demonstration',
  'lifestyle',
  'other',
] as const;
export const conceptGenreSchema = z.enum(PRODUCTION_CONCEPT_GENRES);

/**
 * REKLAM BİÇİMİ (Dalga 4, 2 Eki 2026) — fikrin kanıtlanmış reklam kalıbı.
 * Kullanıcı kararı (plan §4): "fikir üretimi kanıtlanmış reklam biçimlerini de
 * kapsar (problem→çözüm, ürün gösterimi, UGC tarzı, kampanya, sosyal kanıt,
 * kutu açılışı…) — her şey reklam performansı için". Tür (`genre`) fikrin
 * ANLATIM tonu (dram, komedi…), biçim reklamın SATIŞ mekaniği; ekran fikir
 * kartında biçimi gösterir (tür yalnız "Ben yöneteyim"de).
 *
 * Biçimin DOĞRULUĞU worker'da (`brief/ad-formats.ts` → `formatBlocker`):
 * `offer` yalnız mağazanın gerçek fiyatı/indirimiyle, `social_proof` yalnız
 * dosyadaki gerçek yorum alıntısıyla. Akıl şeridi alan sözleşmede yokken
 * biçimi `whyItWorks`ün başına önek olarak yazıyordu ("Problem → çözüm · …");
 * entegrasyonda alan oldu, önek kalktı.
 */
export const PRODUCTION_AD_FORMATS = [
  'insight_story',
  'problem_solution',
  'demonstration',
  'ugc',
  'offer',
  'social_proof',
  'first_use',
  'comparison',
  'routine',
  'seasonal',
] as const;
export type ProductionAdFormat = (typeof PRODUCTION_AD_FORMATS)[number];
export const productionAdFormatSchema = z.enum(PRODUCTION_AD_FORMATS);

/**
 * KONSEPT — 3–5 adet, her biri bir İÇGÖRÜ cümlesiyle başlar. Klişe puanı
 * 0–10 (10 = tamamen klişe; yüksek olan elenir ya da dönüştürülür), politika
 * notları ön denetimin çıktısı.
 */
export const conceptSchema = z.object({
  id: shortIdSchema,
  title: z.string().trim().min(1).max(80),
  insight: z.string().trim().min(1).max(240),
  genre: conceptGenreSchema,
  /// Reklam biçimi — Dalga 4'ten önce yazılmış fikirde YOK (satır göçü yok).
  format: productionAdFormatSchema.optional(),
  logline: z.string().trim().min(1).max(300),
  whyItWorks: z.string().trim().min(1).max(400),
  hookIdea: z.string().trim().min(1).max(240),
  productRole: z.string().trim().min(1).max(240),
  tone: z.string().trim().min(1).max(120),
  clicheScore: z.number().int().min(0).max(10),
  clicheNotes: z.string().trim().max(300),
  policyNotes: z.array(z.string().trim().min(1).max(240)).max(6),
  musicDirection: z.string().trim().min(1).max(240),
  voDirection: z.string().trim().max(240).nullable(),
  /// DECIDE modunda direktörün seçtiği — gerekçesiyle.
  recommended: z.boolean().default(false),
  recommendationReason: z.string().trim().max(400).nullable().default(null),
  /**
   * `false` = fikir dürüstçe kurulamayan bir biçimle yazıldı (fiyatsız teklif,
   * yorumsuz sosyal kanıt) ve listeyi 3'e tamamlamak için içgörü hikâyesi
   * olarak geri alındı. Seçim (DECIDE hakemi, kullanıcının çok fikirli seti)
   * önce diğerlerine bakar. Yoksa/`true` = kullanılabilir.
   */
  formatAvailable: z.boolean().optional(),
});
export type ProductionConcept = z.infer<typeof conceptSchema>;
export const PRODUCTION_CONCEPT_COUNT = { min: 3, max: 5 } as const;

export const SCRIPT_BEAT_ROLES = ['hook', 'tension', 'turn', 'product', 'payoff', 'cta'] as const;
export type ScriptBeatRole = (typeof SCRIPT_BEAT_ROLES)[number];

export const scriptBeatSchema = z
  .object({
    id: shortIdSchema,
    role: z.enum(SCRIPT_BEAT_ROLES),
    startSec: z.number().min(0).max(120),
    endSec: z.number().min(0).max(120),
    description: z.string().trim().min(1).max(400),
    emotion: z.string().trim().min(1).max(80),
    /// Dış ses satırı — ~12,7 karakter/sn ile bütçelenir (REPORT §6.2).
    vo: z.string().trim().max(300).optional(),
    /// Ekrana basılacak üst yazı — gerçek fontla bindirme, model çizmez.
    super: z.string().trim().max(80).optional(),
    sound: z.string().trim().max(160).optional(),
    /// Kısa kesimde (`cutdowns`) atılabilir mi.
    droppable: z.boolean().default(false),
  })
  .refine((beat) => beat.endSec > beat.startSec, { message: 'endSec > startSec olmalı', path: ['endSec'] });
export type ScriptBeat = z.infer<typeof scriptBeatSchema>;

/**
 * Müzik YÖNÜ — dikte değil. 🔴 Faz 1'de yön tersine döndü: müzik modelleri
 * BPM/yapı/süreye güvenilir uymuyor (Lyria 15 sn → 152 sn; EL 12. sn'de
 * bitiyor). Müzik reklamdan UZUN üretilir, gerçek vuruş ızgarası ÖLÇÜLÜR ve
 * kurgu MÜZİĞE kesilir. Buradaki `bpm`/`structure` prompt'a niyet olarak
 * gider; zamanlamanın kaynağı ölçüm.
 */
export const scriptMusicSchema = z.object({
  bpm: z.number().int().min(40).max(220).optional(),
  key: z.string().trim().max(24).optional(),
  mood: z.string().trim().min(1).max(160),
  /// Tam saniye — Lyria ondalıklı zaman damgasını 422 ile reddediyor.
  structure: z
    .array(
      z.object({
        startSec: z.number().int().min(0).max(120),
        endSec: z.number().int().min(0).max(120),
        label: z.string().trim().min(1).max(60),
      }),
    )
    .max(12)
    .default([]),
  instrumentalOnly: z.boolean().default(true),
});

export const scriptVoiceoverSchema = z.object({
  enabled: z.boolean(),
  voice: z.string().trim().max(64).nullable(),
  direction: z.string().trim().max(240).nullable(),
});

export const scriptCtaSchema = z.object({
  text: z.string().trim().min(1).max(60),
  slogan: z.string().trim().max(80).nullable(),
});

/// SENARYO — hook (ilk 1–1,5 sn) → gerilim → dönüş → ürün → payoff → CTA.
export const scriptSchema = z.object({
  durationSec: z.number().int().min(1).max(120),
  beats: z.array(scriptBeatSchema).min(1).max(24),
  emotionCurve: z
    .array(z.object({ atSec: z.number().min(0).max(120), level: z.number().int().min(0).max(10) }))
    .max(24)
    .default([]),
  music: scriptMusicSchema,
  voiceover: scriptVoiceoverSchema,
  cta: scriptCtaSchema,
});
export type ProductionScript = z.infer<typeof scriptSchema>;

/**
 * Dış ses ÜRETİLECEK Mİ — senaryo varsa ONUN kararı, yoksa istek.
 *
 * Senaryo adımı ve senaryo düzenlemesi (`PATCH /script {voiceover}`) dış sesi
 * açıp kapatabiliyor; worker üretimi senaryodan okuyor. Onay tahmini istekten
 * okuyordu → kullanıcının rıza verdiği "en fazla X" üretilecek dış sesi (2×
 * TTS + STT denetimleri) kapsamıyordu, fark bizde kalıyordu (denetim bulgusu,
 * 1 Eki 2026). API tahmini ve worker anlık görüntüsü bu TEK kuralı okuyor.
 */
export function productionVoiceoverEnabled(
  input: { voiceover?: { enabled?: boolean } | null } | null | undefined,
  script: Pick<ProductionScript, 'voiceover'> | null | undefined,
): boolean {
  if (script) return script.voiceover.enabled;
  return input?.voiceover?.enabled === true;
}

// ═══════════════════════════════════════════════════════════════════════════
// ÇIKTI BİÇİMİ — VARYANTLAR (Dalga 4, 2 Eki 2026)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Kullanıcı kararı (plan §4): "Çıktının biçimine AI karar verir" — tek reklam,
 * aynı fikrin AÇILIŞ varyantlı A/B seti ya da farklı fikirlerden birkaç
 * reklam; karar gerekçesi ve fiyatıyla onaydan ÖNCE gösterilir. Üç biçim tek
 * modelle anlatılıyor: prodüksiyonun 1..5 VARYANTI var.
 *
 *   single         1 varyant (`single`) — bugünkü davranış
 *   hook_set       aynı fikir, 2..5 farklı AÇILIŞ (`hook`): yalnız açılış
 *                  çekimi(leri) ve açılış üst yazısı değişir, gerisi ORTAK
 *                  (aynı take'ler, aynı müzik, aynı dış ses) — ucuz A/B
 *   multi_concept  2..3 farklı FİKİR (`concept`): her biri kendi senaryosu,
 *                  çekimleri, müziği ve dış sesiyle ayrı bir reklam
 *
 * Çekim satırı hangi varyantlara ait olduğunu taşır (`ProductionShot.variantKeys`):
 * BOŞ = bütün varyantların ortak çekimi; açılış varyantının çekimi yalnız kendi
 * varyantını; çok fikirlide her çekim kendi fikrinin varyantını listeler.
 * Kurgu/son/teslim varyant başına koşar; teslim adı varyant ekini taşır
 * (`productionRendition({ variant })`: `master.v2`, `4x5.v2`, `9x16.cut6.v2`).
 */
export const PRODUCTION_STRATEGIES = ['single', 'hook_set', 'multi_concept'] as const;
export type ProductionStrategy = (typeof PRODUCTION_STRATEGIES)[number];
export const productionStrategySchema = z.enum(PRODUCTION_STRATEGIES);

export const PRODUCTION_VARIANT_KINDS = ['single', 'hook', 'concept'] as const;
export type ProductionVariantKind = (typeof PRODUCTION_VARIANT_KINDS)[number];

/// Biçimin varyant sayısı sınırları — kullanıcının değişikliği (`setStrategy`) dahil.
export const PRODUCTION_STRATEGY_LIMITS: Readonly<
  Record<ProductionStrategy, { min: number; max: number }>
> = {
  single: { min: 1, max: 1 },
  hook_set: { min: 2, max: PRODUCTION_MAX_VARIANTS },
  /// Üç tam reklam = üç kat çekim; daha fazlası ayrı prodüksiyon olmalı.
  multi_concept: { min: 2, max: 3 },
};

/**
 * AI'ın KENDİ BAŞINA seçebileceği tavan — başlarken rezerve edilen storyboard
 * evresi bu zarfı kapsıyor (`production-pricing` → `storyboardEstimateRecord`).
 * Daha büyüğünü yalnız kullanıcı seçer, ek tutarı görüp onaylayarak.
 *
 * 🔴 Kullanıcı kararı (2 Eki 2026, "taslak rezervi"): AI en fazla TEK fikir +
 * açılış seti (≤ 3) seçer; birkaç fikri YALNIZ kullanıcı seçer. Biçimin alt
 * sınırının altındaki tavan (`multi_concept: 1` < 2) "AI bu biçimi hiç seçmez"
 * demek (`productionAutoStrategyAllowed`). 15 sn DECIDE örneğinde başta
 * kilitlenen tutar 204 → 146 kredi (açılış seti 3, `autoPlanShapes`).
 * Kullanıcının zarfı aşan seçimi taslaktan ÖNCE ayrı rıza + rezerv ister
 * (`productionReservationKey(id, { plan: n })`, `productionSetStrategySchema.confirmCredits`).
 */
export const PRODUCTION_AUTO_STRATEGY_LIMITS: Readonly<Record<ProductionStrategy, number>> = {
  single: 1,
  hook_set: 3,
  multi_concept: 1,
};

/**
 * AI bu biçimi (bu varyant sayısıyla) KENDİ BAŞINA seçebilir mi — biçimin
 * sınırları içinde VE AI tavanının altında. `variantCount` verilmezse biçimin
 * en küçüğü sorulur ("bu biçim AI'a açık mı").
 */
export function productionAutoStrategyAllowed(
  strategy: ProductionStrategy,
  variantCount: number = PRODUCTION_STRATEGY_LIMITS[strategy].min,
): boolean {
  const limits = PRODUCTION_STRATEGY_LIMITS[strategy];
  return (
    variantCount >= limits.min &&
    variantCount <= limits.max &&
    variantCount <= PRODUCTION_AUTO_STRATEGY_LIMITS[strategy]
  );
}

/**
 * Türün seçebileceği biçimler — görsel kampanya YALNIZ tek set (kullanıcı
 * kararı: "strateji görsel kampanyada tek"; seri/karusel/banner zaten çoklu
 * çıktı). API `setStrategy` görsel kampanyada reddeder, makine strateji
 * adımını hiç koşturmaz.
 */
export const PRODUCTION_KIND_STRATEGIES: Readonly<
  Record<ProductionKind, readonly ProductionStrategy[]>
> = {
  VIDEO: PRODUCTION_STRATEGIES,
  IMAGE_SET: ['single'],
};

/// Onaydan önce kullanıcının biçimi değiştirme hakkı (her değişiklik yeni
/// taslak kareler üretebilir — storyboard rezervi kapandıktan sonra).
export const PRODUCTION_MAX_USER_STRATEGY_CHANGES = 3;

export const productionVariantSchema = z.object({
  key: productionVariantKeySchema,
  kind: z.enum(PRODUCTION_VARIANT_KINDS),
  /// Varyantın fikri (`conceptSchema.id`). `v1` her zaman seçilen fikir.
  conceptId: shortIdSchema,
  /**
   * Açılış varyantının AÇILIŞ beat'i — `v1`de birincil senaryonun hook
   * beat'i, `v2…`de `variantScripts[key].hookBeat.id`. Açılış yazıcısı
   * (`hooks` adımı) yazdıktan sonra dolar; o zamana kadar yok.
   */
  hookBeatId: shortIdSchema.optional(),
  /// Kullanıcının dilinde kısa ad ("Ateş ölçen el", "Sosyal kanıt").
  label: z.string().trim().min(1).max(80),
  /// Kullanıcının dilinde — bu varyant neden var.
  rationale: z.string().trim().max(400),
});
export type ProductionVariant = z.infer<typeof productionVariantSchema>;

/**
 * ÇIKTI PLANI (`CreativeProduction.deliverablePlan`) — strateji adımının
 * kararı (`decidedBy: 'ai'`), kullanıcı onay ekranında değiştirebilir
 * (`productionSetStrategySchema` → `decidedBy: 'user'`).
 *
 * `lengths`/`aspects` ETKİN çıktılar: ana süre (senaryo bunu tutar), kısa
 * kesimler ve ek boyutlar. Kullanıcının istediği hiçbir şey düşmez
 * (`deliverablePlanIssues` zorlar); AI yalnız EKLEYEBİLİR (ör. YouTube için
 * 6 sn bumper). Kurgu/son bunları istekten değil buradan okur (`planOutputs`).
 */
export const deliverablePlanSchema = z
  .object({
    strategy: productionStrategySchema,
    variants: z.array(productionVariantSchema).min(1).max(PRODUCTION_MAX_VARIANTS),
    lengths: z.object({
      main: z.number().int().min(1).max(120),
      cutdowns: z.array(productionDurationSchema).max(3),
    }),
    aspects: z.object({
      main: productionAspectSchema,
      extra: z.array(productionAspectSchema).max(PRODUCTION_ASPECTS.length - 1),
    }),
    /// Kullanıcının dilinde — "neden bu biçim" (onaydan önce gösterilir).
    rationale: z.string().trim().min(1).max(600),
    decidedBy: z.enum(['ai', 'user']),
    /// Kullanıcının biçim değişikliği sayısı (`PRODUCTION_MAX_USER_STRATEGY_CHANGES`).
    userChanges: z.number().int().min(0).max(100).default(0),
  })
  .superRefine((plan, ctx) => {
    for (const issue of planShapeIssues(plan)) {
      ctx.addIssue({ code: 'custom', path: [issue.path], message: issue.message });
    }
  });
export type DeliverablePlan = z.infer<typeof deliverablePlanSchema>;
export type DeliverablePlanInput = z.input<typeof deliverablePlanSchema>;

/// Planın İÇ tutarlılığı (bağlamsız) — şemanın `superRefine`ı.
function planShapeIssues(
  plan: Pick<DeliverablePlan, 'strategy' | 'variants' | 'lengths' | 'aspects'>,
): { path: string; message: string }[] {
  const issues: { path: string; message: string }[] = [];
  const { variants } = plan;
  variants.forEach((variant, index) => {
    if (variant.key !== PRODUCTION_VARIANT_KEYS[index]) {
      issues.push({ path: 'variants', message: `Varyant anahtarları sırayla v1…: ${variant.key}` });
    }
  });
  const limits = PRODUCTION_STRATEGY_LIMITS[plan.strategy];
  if (variants.length < limits.min || variants.length > limits.max) {
    issues.push({
      path: 'variants',
      message: `${plan.strategy}: ${limits.min}–${limits.max} varyant (${variants.length})`,
    });
  }
  const kind: ProductionVariantKind =
    plan.strategy === 'single' ? 'single' : plan.strategy === 'hook_set' ? 'hook' : 'concept';
  if (variants.some((variant) => variant.kind !== kind)) {
    issues.push({ path: 'variants', message: `${plan.strategy} varyantları ${kind} türünde olmalı` });
  }
  const concepts = new Set(variants.map((variant) => variant.conceptId));
  if (plan.strategy === 'hook_set' && concepts.size !== 1) {
    issues.push({ path: 'variants', message: 'Açılış varyantları AYNI fikirden' });
  }
  if (plan.strategy === 'multi_concept' && concepts.size !== variants.length) {
    issues.push({ path: 'variants', message: 'Çok fikirli setin fikirleri farklı olmalı' });
  }
  const cutdowns = plan.lengths.cutdowns;
  if (new Set(cutdowns).size !== cutdowns.length || cutdowns.some((cut) => cut >= plan.lengths.main)) {
    issues.push({ path: 'lengths', message: 'Kesimler tekil ve ana süreden kısa olmalı' });
  }
  const extra = plan.aspects.extra;
  if (new Set(extra).size !== extra.length || extra.includes(plan.aspects.main)) {
    issues.push({ path: 'aspects', message: 'Ek boyutlar tekil ve ana boyuttan farklı olmalı' });
  }
  return issues;
}

/**
 * Planın PRODÜKSİYONA uyumu (bağlamlı) — strateji adımının çıktısı (worker
 * yürütücüsü) ve kullanıcının değişikliği (API `setStrategy`) AYNI kuralla
 * sınanır. Boş liste = uyumlu.
 *   · `v1`in fikri seçilen fikir; her fikir kimliği konsept listesinde
 *   · ana boyut istekteki boyut; kullanıcının ek boyutları/kesimleri planda
 *     (AI yalnız ekler, düşürmez); ek boyutlar yerleşime uygun
 *   · istek süreyi verdiyse ana süre o
 */
export function deliverablePlanIssues(
  plan: Pick<DeliverablePlan, 'strategy' | 'variants' | 'lengths' | 'aspects'>,
  context: {
    chosenConceptId: string | null;
    conceptIds: readonly string[];
    input: Pick<
      ProductionStartInput,
      'aspect' | 'placement' | 'durationSeconds' | 'extraAspects' | 'cutdowns'
    >;
  },
): string[] {
  const issues = planShapeIssues(plan).map((issue) => issue.message);
  const first = plan.variants[0];
  if (!first || first.conceptId !== context.chosenConceptId) {
    issues.push('v1 seçilen fikir olmalı');
  }
  const known = new Set(context.conceptIds);
  for (const variant of plan.variants) {
    if (!known.has(variant.conceptId)) issues.push(`Bilinmeyen fikir: ${variant.conceptId}`);
  }
  const { input } = context;
  if (plan.aspects.main !== input.aspect) issues.push('Ana boyut istekteki boyut olmalı');
  for (const aspect of input.extraAspects) {
    if (!plan.aspects.extra.includes(aspect)) issues.push(`İstenen ek boyut düşmüş: ${aspect}`);
  }
  for (const aspect of plan.aspects.extra) {
    if (!placementAllowsAspect(input.placement, aspect) && !input.extraAspects.includes(aspect)) {
      issues.push(`Ek boyut yerleşime uymuyor: ${aspect}`);
    }
  }
  for (const cut of input.cutdowns) {
    if (!plan.lengths.cutdowns.includes(cut)) issues.push(`İstenen kesim düşmüş: ${cut}`);
  }
  if (input.durationSeconds !== null && plan.lengths.main !== input.durationSeconds) {
    issues.push('Ana süre istekteki süre olmalı');
  }
  return issues;
}

/**
 * Plan YOKKEN (strateji adımından önce açılmış ya da göç öncesi prodüksiyon)
 * geçerli olan tek-reklam planı — davranış bugünküyle aynı.
 */
/// Süre değeri `PRODUCTION_DURATIONS`tan mı (kesim listesi sayı dizisinden süzülürken).
export function isProductionDuration(value: number): value is ProductionDuration {
  return (PRODUCTION_DURATIONS as readonly number[]).includes(value);
}

export function singleDeliverablePlan(params: {
  conceptId: string;
  label: string;
  rationale: string;
  input: { aspect: ProductionAspect; extraAspects: readonly ProductionAspect[]; cutdowns: readonly number[] };
  mainSeconds: number;
  decidedBy?: DeliverablePlan['decidedBy'];
}): DeliverablePlan {
  return {
    strategy: 'single',
    variants: [
      {
        key: PRODUCTION_PRIMARY_VARIANT,
        kind: 'single',
        conceptId: params.conceptId,
        label: params.label.slice(0, 80) || PRODUCTION_PRIMARY_VARIANT,
        rationale: params.rationale.slice(0, 400),
      },
    ],
    lengths: {
      main: params.mainSeconds,
      cutdowns: params.input.cutdowns
        .filter(isProductionDuration)
        .filter((cut) => cut < params.mainSeconds),
    },
    aspects: {
      main: params.input.aspect,
      extra: params.input.extraAspects.filter((aspect) => aspect !== params.input.aspect),
    },
    rationale: params.rationale.slice(0, 600) || '—',
    decidedBy: params.decidedBy ?? 'ai',
    userChanges: 0,
  };
}

/// Planın varyant anahtarları; plan yoksa yalnız `v1`.
export function planVariantKeys(
  plan: Pick<DeliverablePlan, 'variants'> | null | undefined,
): ProductionVariantKey[] {
  return plan ? plan.variants.map((variant) => variant.key) : [PRODUCTION_PRIMARY_VARIANT];
}

/**
 * Varyantın SENARYO/SES SAHİBİ — bu varyantın senaryosu, müziği ve dış sesi
 * hangi varyantınki. Yalnız çok fikirli setin `v2…` varyantları kendi
 * senaryosuna (ve sesine) sahip; tek reklam ve açılış varyantları `v1`inkini
 * paylaşır (açılış varyantı GÖRSEL + üst yazı varyantıdır, dış ses ortak).
 */
export function variantAudioOwner(
  plan: Pick<DeliverablePlan, 'variants'> | null | undefined,
  key: ProductionVariantKey,
): ProductionVariantKey {
  const variant = plan?.variants.find((item) => item.key === key);
  return variant?.kind === 'concept' && key !== PRODUCTION_PRIMARY_VARIANT
    ? key
    : PRODUCTION_PRIMARY_VARIANT;
}

/// Çekim bu varyantın filminde mi: boş liste = ortak çekim.
export function shotInVariant(
  variantKeys: readonly string[] | null | undefined,
  key: ProductionVariantKey,
): boolean {
  return !variantKeys || variantKeys.length === 0 || variantKeys.includes(key);
}

/**
 * VARYANT SENARYOSU (`CreativeProduction.variantScripts`, anahtar = varyant):
 *   hook     açılış varyantının ALTERNATİF hook beat'i — birincil senaryonun
 *            hook beat'inin YERİNE geçer (aynı zaman aralığı, aynı dış ses;
 *            `variantScriptView` zamanı ve `vo`yu birincilden kopyalar)
 *   concept  çok fikirli setin kendi tam senaryosu
 * `v1` burada YOK — birincil senaryo `CreativeProduction.script`.
 */
export const productionVariantScriptSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('hook'), hookBeat: scriptBeatSchema }),
  z.object({ kind: z.literal('concept'), script: scriptSchema }),
]);
export type ProductionVariantScript = z.infer<typeof productionVariantScriptSchema>;
export const productionVariantScriptsSchema = z.partialRecord(
  productionVariantKeySchema,
  productionVariantScriptSchema,
);
export type ProductionVariantScripts = z.infer<typeof productionVariantScriptsSchema>;

/// Birincil senaryonun hook beat'i — açılış varyantlarının "yuvası".
export function primaryHookBeat(script: Pick<ProductionScript, 'beats'>): ScriptBeat | null {
  return script.beats.find((beat) => beat.role === 'hook') ?? null;
}

/**
 * Bir varyantın GÖRDÜĞÜ senaryo — kurgu, son, ses ve çekim listesi bunu okur.
 *   `v1` / tek reklam   birincil senaryo
 *   açılış varyantı      birincil senaryo, hook beat'inin İÇERİĞİ (açıklama,
 *                        duygu, üst yazı, ses) alternatifinkiyle değişmiş;
 *                        KİMLİK, zaman ve `vo` birincilinki — çekimler yuvaya
 *                        (birincilin hook beat kimliğine) bağlı, dış ses ortak
 *   fikir varyantı       kendi senaryosu; yoksa `null` (henüz yazılmadı)
 */
export function variantScriptView(
  script: ProductionScript | null,
  variantScripts: ProductionVariantScripts | null | undefined,
  key: ProductionVariantKey,
): ProductionScript | null {
  if (key === PRODUCTION_PRIMARY_VARIANT) return script;
  const own = variantScripts?.[key];
  if (!own) return null;
  if (own.kind === 'concept') return own.script;
  if (!script) return null;
  const slot = primaryHookBeat(script);
  if (!slot) return script;
  const alternative = own.hookBeat;
  return {
    ...script,
    beats: script.beats.map((beat) => {
      if (beat.id !== slot.id) return beat;
      // Kimlik, zaman, `vo`, `droppable` yuvanın; içerik alternatifin.
      const { super: _super, sound: _sound, ...kept } = beat;
      return {
        ...kept,
        description: alternative.description,
        emotion: alternative.emotion,
        ...(alternative.super ? { super: alternative.super } : {}),
        ...(alternative.sound ? { sound: alternative.sound } : {}),
      };
    }),
  };
}

/**
 * Platformun (Mentorivo) kendi işareti mi — adında ya da özgün dosya adında
 * marka adı geçen logo. Kütüphaneye yanlışlıkla yüklenmiş (demo alanı, test)
 * platform logosu müşterinin reklamına GİRMEZ (plan §6b): ne son karta, ne
 * ANLA adımına "reklamverenin logosu" diye (marka rengi oradan türüyor), ne
 * de başlatma isteğine. Tek tanım — API, worker ve sohbet aracı bunu okur.
 */
export function isPlatformMark(logo: { name: string; originalName: string | null }): boolean {
  return /mentorivo/i.test(`${logo.name} ${logo.originalName ?? ''}`);
}

/**
 * ETKİN çıktılar — plan varsa onun (AI ekleyebilir), yoksa isteğin. Kurgu/son
 * ve fiyat aynı fonksiyonu okur.
 */
export function planOutputs(
  input: { extraAspects: readonly ProductionAspect[]; cutdowns: readonly number[] },
  plan: Pick<DeliverablePlan, 'lengths' | 'aspects'> | null | undefined,
): { extraAspects: ProductionAspect[]; cutdowns: number[] } {
  if (!plan) return { extraAspects: [...input.extraAspects], cutdowns: [...input.cutdowns] };
  return { extraAspects: [...plan.aspects.extra], cutdowns: [...plan.lengths.cutdowns] };
}

/**
 * TESLİM UYARISI (`Creative.inputParams.warnings`, `ProductionDeliverableView.warnings`) —
 * ücretli prodüksiyonu DÜŞÜRMEK yerine eksik bir parçayla teslim edildi.
 *   music_unavailable  bütün müzik denemeleri vokalli çıktı (EL, katı EL,
 *                      Lyria, Stable Audio 3) — reklam müziksiz (dış ses +
 *                      ses tasarımı) teslim edildi (kullanıcı kararı, 2 Eki 2026)
 *
 * Görsel kampanya FINISH QA'sı (Dalga 5b) — set DÜŞMEZ, kusur söylenir
 * (worker `image-set/qa-warnings.ts`, kural `composeCopy`nin `ok` hesabı):
 *   copy_over_product  bir yazı ürünle eşikten fazla kesişiyor / ürünün başına değiyor
 *   copy_over_subject  başlık/gövde KİŞİNİN (yüz, saç) üstüne basıldı — düzen
 *                      ürün ∪ kişi kutusunun etrafında yer bulamadı (2 Eki denetimi)
 *   copy_low_contrast  ölçülen kontrast eşiğin altında ya da perde tavanı okunurluğu engelledi
 *   copy_out_of_frame  bir yazı kutusu karenin dışına taşıyor
 *   copy_dropped       başlık, CTA ya da (teklif karesinde) fiyat yer bulamadığı için
 *                      BASILMADI — kare onsuz teslim edildi (bilinçli sadeleştirme,
 *                      ör. küçük banner'da gövde, şeritte fiyat, sayılmaz)
 *   file_over_limit    IAB boyutunda dosya 150 KB tavanına inemedi
 * Dalga 5b'de paralel şeritler ortağa yazamadığı için kodlar önce worker'da
 * duruyordu ve API izdüşümü onları SÜZÜYORDU (ekran/sohbet görmüyordu);
 * entegrasyonda buraya indi. Ekran metinleri: `…production.delivered.warning.*`
 * ve `…agentChat.cards.production.deliverables.warning.*`.
 */
export const PRODUCTION_DELIVERABLE_WARNINGS = [
  'music_unavailable',
  'copy_over_product',
  'copy_over_subject',
  'copy_low_contrast',
  'copy_out_of_frame',
  'copy_dropped',
  'file_over_limit',
] as const;
export type ProductionDeliverableWarning = (typeof PRODUCTION_DELIVERABLE_WARNINGS)[number];

/**
 * KREATİFİN teslim uyarıları (`Creative.inputParams.warnings`) — prodüksiyon
 * teslimininkiler + tek render'ın kendi uyarıları:
 *   quality_weak  hızlı klip kalite denetiminden KIL PAYI geçti (yargıç "zayıf"
 *                 dedi, ikinci take daha iyisini veremedi) — teslim edildi ama
 *                 yayından önce bakılmalı (Dalga 5b entegrasyonu: ekran metni
 *                 vardı, yazan yoktu).
 * Prodüksiyon izdüşümü `PRODUCTION_DELIVERABLE_WARNINGS`le süzmeye devam eder
 * (prodüksiyon teslimi bu kodu taşımaz); iş satırı, sohbet kartı ve
 * görüntüleyici bu listeyle.
 */
export const CREATIVE_DELIVERY_WARNINGS = [
  ...PRODUCTION_DELIVERABLE_WARNINGS,
  'quality_weak',
] as const;
export type CreativeDeliveryWarning = (typeof CREATIVE_DELIVERY_WARNINGS)[number];

// ═══════════════════════════════════════════════════════════════════════════
// GÖRSEL KAMPANYA — kare planı ve teslim adları (Dalga 5, 2 Eki 2026)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Karenin reklamdaki GÖREVİ — seri/karuselin her karesi bir şey satar:
 *   hero       ürünün kahraman görseli (setin kapağı)
 *   detail     doku/malzeme/etiket yakını
 *   lifestyle  kullanım anı, mekân, kişi
 *   benefit    tek fayda, tek cümle
 *   proof      sosyal kanıt — YALNIZ dosyadaki gerçek yorum alıntısı (uydurma yok)
 *   offer      teklif — YALNIZ mağazanın gerçek fiyatı/indirimi
 *   cta        çağrı kartı (karuselin sonu)
 */
export const IMAGE_SET_FRAME_ROLES = [
  'hero',
  'detail',
  'lifestyle',
  'benefit',
  'proof',
  'offer',
  'cta',
] as const;
export type ImageSetFrameRole = (typeof IMAGE_SET_FRAME_ROLES)[number];

/**
 * KARE PLANI'nın bir karesi. `headline`/`body` KARENİN ÜSTÜNE gerçek fontla
 * basılan metin (FINISH, deterministik tipografi — model yazı çizmez); kare
 * YAZISIZ plaka olarak üretilir. `visualBrief` kullanıcının gördüğü ve
 * düzenlediği görsel tarif (sade dil; prompt sunucuda bundan derlenir).
 */
export const imageSetFrameSchema = z.object({
  /// 1'den, sıra = teslim adındaki numara (`set.3`).
  index: z.number().int().min(1).max(IMAGE_SET_MAX_FRAMES),
  role: z.enum(IMAGE_SET_FRAME_ROLES),
  headline: z.string().trim().min(1).max(60).optional(),
  body: z.string().trim().min(1).max(160).optional(),
  visualBrief: z.string().trim().min(1).max(600),
});
export type ImageSetFrame = z.infer<typeof imageSetFrameSchema>;

/**
 * KARE PLANI (`CreativeProduction.setPlan`) — `SET_PLAN` adımının çıktısı;
 * görsel kampanyada senaryo + çekim listesinin YERİNE. Kareler aynı adımda
 * `ProductionShot` satırı olarak da yazılır (sıra `index − 1`): anahtar kare
 * hattı, kare düzenleme ve projeksiyon çekim satırı üzerinden çalışıyor.
 *   SERIES      5–10 kare, her biri tek başına bir reklam görseli
 *   CAROUSEL    3–10 kart + `narrative` (kartların bir arada anlattığı)
 *   BANNER_SET  TEK kare (ana görsel; `hero`) — boyutlara FINISH uyarlar
 * `cta` çağrı düğmesinin metni (son kart / banner); yoksa marka kitinin ya da
 * isteğin son kart CTA'sı.
 */
export const imageSetPlanSchema = z
  .object({
    setType: imageSetTypeSchema,
    frames: z.array(imageSetFrameSchema).min(1).max(IMAGE_SET_MAX_FRAMES),
    narrative: z.string().trim().min(1).max(400).optional(),
    cta: z.string().trim().min(1).max(40).optional(),
  })
  .superRefine((plan, ctx) => {
    for (const issue of imageSetPlanShapeIssues(plan)) {
      ctx.addIssue({ code: 'custom', path: [issue.path], message: issue.message });
    }
  });
export type ImageSetPlan = z.infer<typeof imageSetPlanSchema>;

function imageSetPlanShapeIssues(
  plan: Pick<ImageSetPlan, 'setType' | 'frames' | 'narrative'>,
): { path: string; message: string }[] {
  const issues: { path: string; message: string }[] = [];
  plan.frames.forEach((frame, position) => {
    if (frame.index !== position + 1) {
      issues.push({ path: 'frames', message: `Kare numaraları sırayla 1…: ${frame.index}` });
    }
  });
  if (plan.setType === 'BANNER_SET') {
    if (plan.frames.length !== 1) {
      issues.push({ path: 'frames', message: 'Banner seti tek ana görsel' });
    }
    return issues;
  }
  const limits = IMAGE_SET_COUNT_LIMITS[plan.setType];
  if (plan.frames.length < limits.min || plan.frames.length > limits.max) {
    issues.push({
      path: 'frames',
      message: `${plan.setType}: ${limits.min}–${limits.max} kare (${plan.frames.length})`,
    });
  }
  if (plan.setType === 'CAROUSEL' && !plan.narrative) {
    issues.push({ path: 'narrative', message: 'Karuselin anlatısı gerekli' });
  }
  return issues;
}

/**
 * Planın İSTEĞE uyumu (bağlamlı) — yürütücü `SET_PLAN` çıktısını yazmadan
 * önce bununla sınar. Boş liste = uyumlu. Tür istekteki; kare sayısı
 * `imageSetFrameCount` (banner setinde 1).
 */
export function imageSetPlanIssues(
  plan: Pick<ImageSetPlan, 'setType' | 'frames' | 'narrative'>,
  request: Pick<ImageSetRequest, 'setType' | 'count'>,
): string[] {
  const issues = imageSetPlanShapeIssues(plan).map((issue) => issue.message);
  if (plan.setType !== request.setType) {
    issues.push(`Set türü istekteki olmalı (${request.setType})`);
  }
  const expected = imageSetFrameCount(request);
  if (plan.frames.length !== expected) {
    issues.push(`Kare sayısı ${expected} olmalı (${plan.frames.length})`);
  }
  return issues;
}

/**
 * TESLİM ADLARI — görsel kampanya (`Creative.rendition`, `AD_IMAGE`):
 *   `set.<n>`               serinin n. karesi        (`set.3`)
 *   `carousel.<n>`          karuselin n. kartı       (`carousel.2`)
 *   `banner.<G>x<Y>`        banner setinin bir boyutu (`banner.300x250`)
 * Varyant ve kurgu ekleri videonunkiyle AYNI kural: `[.v<n>][.r<kurgu>]`, kurgu
 * eki hep EN SONDA (`set.3.r1`, `banner.300x250.v2.r1`) — `productionRenditionCut`
 * ve `productionRenditionVariant` bu adları da doğru okur. Görsel kampanya bugün
 * tek varyantlı (`PRODUCTION_KIND_STRATEGIES`); ek kural yine de tutarlı.
 */
export const IMAGE_SET_RENDITION_PREFIX: Readonly<Record<ImageSetType, string>> = {
  SERIES: 'set',
  CAROUSEL: 'carousel',
  BANNER_SET: 'banner',
};

export function imageSetRendition(input: {
  setType: ImageSetType;
  /// SERIES/CAROUSEL: karenin numarası (1'den).
  index?: number;
  /// BANNER_SET: boyut (`BANNER_SET_SIZES[].id`).
  size?: BannerSizeId;
  cut?: number;
  variant?: ProductionVariantKey | null;
}): string {
  const prefix = IMAGE_SET_RENDITION_PREFIX[input.setType];
  let base: string;
  if (input.setType === 'BANNER_SET') {
    if (!input.size || !bannerSetSize(input.size)) {
      throw new Error(`Banner boyutu geçersiz: ${String(input.size)}`);
    }
    base = `${prefix}.${input.size}`;
  } else {
    const index = input.index ?? 0;
    if (!Number.isInteger(index) || index < 1 || index > IMAGE_SET_MAX_FRAMES) {
      throw new Error(`Kare numarası 1–${IMAGE_SET_MAX_FRAMES} olmalı: ${String(input.index)}`);
    }
    base = `${prefix}.${index}`;
  }
  const parts = [base];
  const suffix = variantSuffix(input.variant);
  if (suffix) parts.push(suffix);
  if (input.cut && input.cut > 0) parts.push(`${PRODUCTION_CUT_SUFFIX}${input.cut}`);
  return parts.join('.');
}

const IMAGE_SET_RENDITION =
  /^(set|carousel|banner)\.(\d{1,2}|\d{2,4}x\d{2,4})(?:\.(v[1-9]))?(?:\.r(\d{1,3}))?$/;

/// Görsel kampanya teslim adını çözer; video adı ya da bozuk adda `null`.
export function parseImageSetRendition(rendition: string | null | undefined): {
  setType: ImageSetType;
  index: number | null;
  size: BannerSizeId | null;
  variant: ProductionVariantKey;
  cut: number;
} | null {
  const match = rendition ? IMAGE_SET_RENDITION.exec(rendition) : null;
  if (!match) return null;
  const [, prefix, part, variant, cut] = match;
  const setType = (Object.keys(IMAGE_SET_RENDITION_PREFIX) as ImageSetType[]).find(
    (type) => IMAGE_SET_RENDITION_PREFIX[type] === prefix,
  );
  if (!setType || !part) return null;
  const isBanner = setType === 'BANNER_SET';
  const size = isBanner && bannerSetSize(part) ? (part as BannerSizeId) : null;
  const index = !isBanner && /^\d+$/.test(part) ? Number(part) : null;
  if (isBanner ? size === null : index === null || index < 1 || index > IMAGE_SET_MAX_FRAMES) {
    return null;
  }
  return {
    setType,
    index,
    size,
    variant: (variant as ProductionVariantKey | undefined) ?? PRODUCTION_PRIMARY_VARIANT,
    cut: cut ? Number(cut) : 0,
  };
}

/**
 * SETİN KAPAĞI — geçmişte görünen TEK kart (`CreativeProduction.masterCreativeId`):
 * seri/karuselde ilk kare, banner setinde istenen İLK boyut.
 */
export function imageSetCoverRendition(params: {
  request: Pick<ImageSetRequest, 'setType' | 'sizes'>;
  cut?: number;
  variant?: ProductionVariantKey | null;
}): string {
  const { request } = params;
  const size =
    request.setType === 'BANNER_SET'
      ? (request.sizes?.[0] ?? BANNER_SET_DEFAULT_SIZES[0])
      : undefined;
  return imageSetRendition({
    setType: request.setType,
    ...(size ? { size } : { index: 1 }),
    cut: params.cut ?? 0,
    variant: params.variant ?? null,
  });
}

/**
 * Prodüksiyonun KAPAK teslim adı — geçmiş kartının ve "teslim edildi mi"
 * sorusunun cevabı türden bağımsız tek yerde: video → kurgunun (varyantın)
 * `master`ı (`productionMasterRendition`), görsel kampanya → setin kapağı.
 */
export function productionCoverRendition(params: {
  kind: ProductionKind;
  imageSet: Pick<ImageSetRequest, 'setType' | 'sizes'> | null;
  cut: number;
  variant?: ProductionVariantKey | null;
}): string {
  if (params.kind === 'IMAGE_SET' && params.imageSet) {
    return imageSetCoverRendition({
      request: params.imageSet,
      cut: params.cut,
      variant: params.variant ?? null,
    });
  }
  return productionMasterRendition(params.cut, params.variant ?? null);
}

/**
 * Setin BÜTÜN teslim adları (bu kurgu/varyant) — FINISH'in üretmesi, DELIVER'ın
 * yazması gereken liste: seri/karuselde her kare, banner setinde her boyut.
 */
export function imageSetRenditions(params: {
  request: ImageSetRequest;
  cut?: number;
  variant?: ProductionVariantKey | null;
}): string[] {
  const { request } = params;
  const common = { cut: params.cut ?? 0, variant: params.variant ?? null };
  if (request.setType === 'BANNER_SET') {
    return (request.sizes ?? []).map((size) =>
      imageSetRendition({ setType: request.setType, size, ...common }),
    );
  }
  return Array.from({ length: imageSetFrameCount(request) }, (_, position) =>
    imageSetRendition({ setType: request.setType, index: position + 1, ...common }),
  );
}

export const SHOT_FRAMINGS = [
  'macro',
  'extreme_close_up',
  'close_up',
  'medium_close_up',
  'medium',
  'medium_wide',
  'wide',
  'extreme_wide',
  'overhead',
  'pov',
] as const;
export const SHOT_SPEEDS = ['normal', 'slowmo', 'ramp'] as const;
export const SHOT_TRANSITIONS = ['cut', 'match_cut', 'whip', 'dissolve', 'fade', 'smash_cut'] as const;

/**
 * ÇEKİM SPEC'İ — kullanıcının gördüğü ve düzenlediği nesne. Prompt bundan
 * SUNUCUDA derlenir; spec'in kendisi prompt değil.
 *
 * Teknik alanlar (`lens`, `cameraMove`, `light`, `grade`) serbest metin:
 * DIRECT modunda kullanıcı "3. sahnede ışık daha sıcak" diye düzenliyor.
 * `shotKind` rota sınıfı — değişirse çekim yeni rotayla yeniden üretilir.
 */
export const shotSpecSchema = z.object({
  order: z.number().int().min(0).max(60),
  beatId: shortIdSchema.nullable(),
  durationSec: z.number().min(0.5).max(15),
  purpose: z.string().trim().min(1).max(200),
  description: z.string().trim().min(1).max(600),
  action: z.string().trim().min(1).max(400),
  productInFrame: z.boolean(),
  /// Ürünün GERÇEK kullanım jesti (kapak açılır, tetiğe basılır). Bilinmiyorsa
  /// yok — uydurulmuş jest yasak.
  usageGesture: z.string().trim().max(200).optional(),
  talent: z.string().trim().max(200).optional(),
  framing: z.enum(SHOT_FRAMINGS),
  lens: z.string().trim().min(1).max(80),
  cameraMove: z.string().trim().min(1).max(200),
  light: z.string().trim().min(1).max(200),
  grade: z.string().trim().min(1).max(120),
  speed: z.enum(SHOT_SPEEDS),
  transitionIn: z.enum(SHOT_TRANSITIONS),
  soundEvent: z.string().trim().max(160).nullable(),
  super: z.string().trim().max(80).optional(),
  shotKind: shotKindSchema,
  continuity: z.string().trim().max(300),
});
export type ShotSpec = z.infer<typeof shotSpecSchema>;

export const QA_DEFECT_SEVERITIES = ['critical', 'major', 'minor'] as const;

/**
 * YÖNETMEN GÖZÜ — take (ya da anahtar kare) başına QA hükmü.
 *
 * 🔴 `pass`, `margin`, `humanReview` KODDA hesaplanır, model hesaplamaz
 * (REPORT §7.2): `production-routes` → `qaVerdict`. Model yalnız puanları ve
 * kusurları yazar; oran kapısı (%2) da koddan kritik kusur ekler.
 */
/**
 * KADRAJ ODAĞI — ürünün (ya da çekimin öznesinin) karedeki kutusu, 0–1
 * normalize (`x`,`y` sol üst köşe). Ek boyutlar (4:5, 1:1, 16:9) ve kesimler
 * GÜVENLİ ALANA göre yeniden kadrajlanırken (plan §2.1/11) kırpma penceresi bu
 * kutuyu kesmeyecek şekilde konumlanır. Kaynağı QA'nın konumlayıcı çağrısı
 * (REPORT §7.1 — ürün ve etiket kutusunu zaten buluyor) ya da anahtar karenin
 * kendi ölçümü.
 */
export const focusBoxSchema = z
  .object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    w: z.number().gt(0).max(1),
    h: z.number().gt(0).max(1),
  })
  .refine((box) => box.x + box.w <= 1.0001 && box.y + box.h <= 1.0001, {
    message: 'Kutu karenin dışına taşıyor.',
  });
export type ProductionFocusBox = z.infer<typeof focusBoxSchema>;

/**
 * TAKE'İN EN İYİ PENCERESİ — kurgunun kullanacağı aralık (sn, take'in kendi
 * zamanında). Modeller alt sınırın altına inemiyor (H3 5 sn istenince 5,17 sn)
 * ve take'in başı/sonu çoğu zaman zayıf (sonda el pompada park ediyor —
 * REPORT §2.4); kurgu çekimi bu pencereden kırpar ve vuruşa yuvarlar.
 */
export const takeWindowSchema = z
  .object({
    inSec: z.number().min(0).max(30),
    outSec: z.number().min(0).max(30),
    /**
     * Brief'in ana EYLEMİNİN / ses olayının take'teki anı (yargıç) — pencere
     * onu içerir; kurgu kısa dilimi bu ana göre yerleştirir ve efekt görünen
     * eyleme oturur (açılışın tek "durduran" anı kesilip atılmasın).
     */
    actionSec: z.number().min(0).max(30).optional(),
  })
  .refine((window) => window.outSec > window.inSec, {
    message: 'outSec > inSec olmalı',
    path: ['outSec'],
  });
export type ProductionTakeWindow = z.infer<typeof takeWindowSchema>;

export const takeQaSchema = z.object({
  scores: z.object({
    productIdentity: z.number().min(0).max(10),
    anatomyPhysics: z.number().min(0).max(10),
    textIntegrity: z.number().min(0).max(10),
    motionQuality: z.number().min(0).max(10),
    briefAdherence: z.number().min(0).max(10),
    adWorthiness: z.number().min(0).max(10),
  }),
  defects: z
    .array(
      z.object({
        severity: z.enum(QA_DEFECT_SEVERITIES),
        frameIndex: z.number().int().min(0).max(60).optional(),
        what: z.string().trim().min(1).max(300),
      }),
    )
    .max(20),
  pass: z.boolean(),
  margin: z.number(),
  humanReview: z.boolean(),
  /// Son karenin harf harf okunan etiketi — kimlik reddinin kanıtı.
  transcribedLabel: z.string().max(600).optional(),
  /// Hükmü VEREN model (sunucu tarafı yedek devreye girdiyse kalibre sayılmaz).
  judgeModel: z.string().min(1).max(80),
  /// Kurgunun kullanacağı aralık — yargıcın önerisi (yoksa kurgu kendi seçer).
  bestWindow: takeWindowSchema.optional(),
  /// Ürünün karedeki kutusu (konumlayıcı) — ek boyutlara yeniden kadraj için.
  focusBox: focusBoxSchema.optional(),
  /// Kişi/el kutusu (konumlayıcı, görsel set) — yazı yeri ürün + kişinin birleşimine göre.
  subjectBox: focusBoxSchema.optional(),
});
export type TakeQa = z.infer<typeof takeQaSchema>;

/**
 * ETİKET DENETİMİ — anahtar karede ve take'te etiket satırlarının harf harf
 * karşılaştırması (REPORT §4 kural 2, §7.1). `expected` ürün dosyasının
 * `labelText`i; `transcribed` otomatik düzeltmesiz okuma (ı/İ/i/I, ş/s… AYRI
 * karakter). Tek belirsiz harfe dayanan uyuşmazlık ikinci bir kırpımdan teyit
 * edilir (REPORT Ek D) — `confirmedBySecondCrop`.
 */
export const labelCheckSchema = z.object({
  expected: z.array(z.string().max(160)).max(20),
  transcribed: z.string().max(600),
  mismatches: z
    .array(z.object({ expected: z.string().max(160), read: z.string().max(160) }))
    .max(20),
  confirmedBySecondCrop: z.boolean().optional(),
});
export type ProductionLabelCheck = z.infer<typeof labelCheckSchema>;

/**
 * ANAHTAR KARE ADAYININ ÖLÇÜMLERİ — `ProductionAsset.meta` (KEYFRAME). Hüküm
 * (`TakeQa`, `pass`/`margin` KODDA) çekimin `keyframe` kaydında; burada
 * hükmün dayandığı ölçümler. Anahtar karede hareket yok — yargıç
 * `motionQuality`yi 10 yazar, kural onu okumuyor (`qaVerdict`).
 * 🔴 Prompt BURAYA YAZILMAZ: varlık `meta`sı API'ye çıkmasa da derlenmiş
 * prompt yalnız işleyicinin içinde yaşar (plan §2.5).
 */
export const keyframeCandidateMetaSchema = z.object({
  /// Turun numarası ve turdaki sırası (`productionAssetKey.keyframe` adayı).
  round: z.number().int().min(0),
  candidate: z.number().int().min(0),
  /// Teslim edilen piksel — tam orana kırpıldıktan SONRA (REPORT §2.1/2).
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  /// Modelin ham çıktısının oran sapması (|w/h ÷ hedef − 1|) — kırpmadan önce.
  rawAspectError: z.number().min(0).optional(),
  labelCheck: labelCheckSchema.optional(),
  focusBox: focusBoxSchema.optional(),
});
export type ProductionKeyframeCandidateMeta = z.infer<typeof keyframeCandidateMetaSchema>;

/**
 * MÜZİĞİN ÖLÇÜLEN IZGARASI — `ProductionAsset.meta` (MUSIC).
 *
 * 🔴 Faz 1'de yön tersine döndü (plan §2.6, REPORT §6.1): müzik modelleri
 * BPM/yapı/süreye güvenilir uymuyor → müzik reklamdan UZUN üretilir, gerçek
 * vuruş/downbeat/bölüm ızgarası MÜZİĞİN KENDİSİNDEN ölçülür ve kurgu ona
 * kesilir. Prompt'taki BPM bir niyetti; buradaki ölçüm. Zamanlar sn, artan.
 */
export const musicAnalysisSchema = z.object({
  durationSec: z.number().positive().max(600),
  bpm: z.number().min(30).max(300),
  beats: z.array(z.number().min(0)).max(4000),
  downbeats: z.array(z.number().min(0)).max(1000),
  sections: z
    .array(
      z.object({
        startSec: z.number().min(0),
        endSec: z.number().min(0),
        /// "intro", "drop", "outro"… — ölçümün etiketi, prompt'unki değil.
        label: z.string().trim().max(40).optional(),
        /// Göreli enerji 0–1 (bölümün RMS'i) — hook ve son kart en güçlü sınıra oturur.
        energy: z.number().min(0).max(1).optional(),
      }),
    )
    .max(64),
  /// Ölçülen ton (ör. "D major") — dikte değil.
  key: z.string().trim().max(24).optional(),
  integratedLufs: z.number().min(-70).max(0).optional(),
  truePeakDb: z.number().min(-70).max(6).optional(),
  /// Enstrümantal denetimi: STT'nin bulduğu KELİME sayısı (> 0 → parça reddedilir).
  sttWords: z.number().int().min(0).optional(),
});
export type ProductionMusicAnalysis = z.infer<typeof musicAnalysisSchema>;

/**
 * DIŞ SESİN ZAMANLAMASI — `ProductionAsset.meta` (VOICEOVER). Eleven v4
 * karakter zamanlaması veriyor (`timestamps: true`, `source: 'provider'`);
 * yedek MiniMax vermiyor → kelime zamanları STT'den (`source: 'stt'`).
 * Altyazı (kelime düzeyi) ve cümlelerin kesim ızgarasına yerleşimi bunu okur.
 */
export const voiceoverTimingSchema = z.object({
  durationSec: z.number().positive().max(300),
  source: z.enum(['provider', 'stt']),
  words: z
    .array(
      z.object({
        text: z.string().min(1).max(80),
        startSec: z.number().min(0),
        endSec: z.number().min(0),
      }),
    )
    .max(2000),
  chars: z
    .array(
      z.object({
        char: z.string().min(1).max(8),
        startSec: z.number().min(0),
        endSec: z.number().min(0),
      }),
    )
    .max(10000)
    .optional(),
  /// Senaryo satırlarının (beat'in `vo`su) ses içindeki yeri.
  lines: z
    .array(
      z.object({
        beatId: shortIdSchema.nullable(),
        text: z.string().min(1).max(300),
        startSec: z.number().min(0),
        endSec: z.number().min(0),
      }),
    )
    .max(48)
    .optional(),
  integratedLufs: z.number().min(-70).max(0).optional(),
});
export type ProductionVoiceoverTiming = z.infer<typeof voiceoverTimingSchema>;

/**
 * SORU (ASK modu) — yalnız kararı değiştiren 2–4 soru; şıklar önizleme
 * kareli. Cevap ya şık (`answerOptionId`) ya serbest metin.
 */
export const questionSchema = z.object({
  id: shortIdSchema,
  text: z.string().trim().min(1).max(240),
  options: z
    .array(
      z.object({
        id: shortIdSchema,
        label: z.string().trim().min(1).max(120),
        /// `ProductionAsset.id` (OPTION_PREVIEW).
        previewAssetId: z.uuid().optional(),
        /**
         * Önizleme karesinin TARİFİ — 🔴 YALNIZ SUNUCU. Soru adımı şıkkı
         * yazarken karesini de tarif ediyor; kareyi üreten işleyici bunu
         * okuyor. DB'de (`questions` JSON) kalır, hiçbir yanıta girmez:
         * projeksiyon şıkkı alan alan kuruyor (`ProductionQuestionView` —
         * `id`, `label`, `previewUrl`). Şemada olmak ZORUNDA: API cevabı
         * yazarken soruları bu şemadan geçirip geri yazıyor ve şemada
         * olmayan alan sessizce düşerdi.
         */
        previewPrompt: z.string().trim().min(1).max(1200).optional(),
      }),
    )
    .min(2)
    .max(4),
  answerOptionId: shortIdSchema.optional(),
  answerText: z.string().trim().max(300).optional(),
});
export type ProductionQuestion = z.infer<typeof questionSchema>;
export const PRODUCTION_QUESTION_COUNT = { min: 2, max: 4 } as const;

export const PRODUCTION_REVISION_STATUSES = [
  'pending',
  'planned',
  'awaiting_confirm',
  'applied',
  'rejected',
] as const;
export type ProductionRevisionStatus = (typeof PRODUCTION_REVISION_STATUSES)[number];

/**
 * `rejected` revizyonun SEBEBİ.
 *   declined         kullanıcı ek kredi rızasını vermedi (`REVISION_CONFIRM` → reddet)
 *   no_active_shots  uygulansa etkin çekim kalmayacaktı — revizyon "hepsini çıkar"
 *                    diyemez (yoksa terminal hatanın TAM iadesi, ödenmiş take'leri
 *                    bedavaya getirirdi; denetim bulgusu)
 *   invalid_output   yönlendiricinin çıktısı sözleşmeye uymadı
 *   failed           yönlendirici kalıcı hata verdi
 * 🔴 Revizyon BAŞARISIZLIĞI prodüksiyonu düşürmez: yalnız revizyon reddedilir,
 * prodüksiyon kaldığı yerden sürer. Talimat kullanıcının metni; prodüksiyonu
 * terminal hataya (= tam iade) düşürebilseydi, çekilmiş bir filmi bedavaya
 * getirmenin yolu olurdu.
 */
export const PRODUCTION_REVISION_REJECT_REASONS = [
  'declined',
  'no_active_shots',
  'invalid_output',
  'failed',
  /// Rıza kapısı `PRODUCTION_GATE_IDLE_CANCEL_DAYS` gün bekledi — süpürücü reddetti.
  'expired',
  /// Teslimden sonraki revizyon hiçbir çıktıyı değiştirmiyordu — yeni kurgu
  /// açılmaz (aynı filmi yeni adla yeniden teslim etmek anlamsız).
  'no_change',
  /// Talimat politika ön denetiminden geçemedi (ör. kanıtsız sağlık iddiası
  /// istendi) — film olduğu gibi kalır.
  'policy_blocked',
  /// Yönlendirici talimatı bu motorla yapılabilir bir değişikliğe eşleyemedi.
  'unsupported',
] as const;
export type ProductionRevisionRejectReason = (typeof PRODUCTION_REVISION_REJECT_REASONS)[number];

/// Revizyon yönlendiricisinin çıktısı — talimatın dokunduğu aşama/çekimler.
export const revisionPlanSchema = z.object({
  summary: z.string().trim().min(1).max(400),
  stages: z.array(productionStageSchema).max(12),
  shotIds: z.array(z.uuid()).max(60),
});

/**
 * REVİZYON — doğal dille ("daha lüks", "müziği değiştir"). Yönlendirici
 * talimatı etkilenen aşama/çekimlere eşler, gerisi korunur. Onaydan sonra
 * maliyetliyse ek rıza (`awaiting_confirm`, kendi rezervi `…:rev:<n>`).
 *
 * `costDeltaUsd` SUNUCUDA kalır; yanıtta kredi (`ProductionRevisionView`).
 */
export const revisionSchema = z.object({
  id: shortIdSchema,
  instruction: z.string().trim().min(1).max(500),
  /// Kullanıcının yer İPUCU (`productionReviseSchema.scope` — "3. sahne",
  /// çekime kapsamlı yeniden çekim talimatı). Yönlendirici bağlı değil;
  /// kararı `plan`a yazar. İstekte yoksa alan da yok.
  scope: z
    .object({
      stage: productionStageSchema.optional(),
      shotIds: z.array(z.uuid()).max(60).optional(),
    })
    .optional(),
  status: z.enum(PRODUCTION_REVISION_STATUSES),
  plan: revisionPlanSchema.optional(),
  costDeltaUsd: z.number().nonnegative().optional(),
  /// Ek rezervin anahtar numarası (`productionReservationKey(id, {revision})`).
  reservationRevision: z.number().int().min(1).optional(),
  /// Ek rezervin defter KİMLİĞİ — rıza anında yazılır. Kapanış önce buna
  /// bakıyor: anahtar deneme eki taşıdığı için numaradan yeniden kurulamaz.
  reservationId: z.string().min(1).max(64).optional(),
  /// Uygulanınca geçersizlenen ara ürün türleri (yönlendiricinin
  /// `invalidate`i). Ses ve referans setinin "dönemi" bundan TÜRÜYOR
  /// (worker `assetEpoch`): sürüm her düzenlemede artıyor, dönem yalnız o
  /// türü gerçekten değiştiren revizyonda.
  invalidated: z.array(productionAssetKindSchema).max(14).optional(),
  rejectedReason: z.enum(PRODUCTION_REVISION_REJECT_REASONS).optional(),
  /**
   * Teslim EDİLMİŞ prodüksiyonda istendi (API yazar; işleyici değiştiremez).
   * Uygulanırsa YENİ KURGU açar (`productionCutIndex`): sürüm artar, teslim
   * damgası (`deliveredAt`) boşalır ve yeni teslimler `.r<n>` ekiyle yazılır.
   * Reddedilirse prodüksiyon önceki teslimine (DELIVERED) döner.
   */
  postDelivery: z.boolean().optional(),
  createdAt: z.iso.datetime(),
});
export type ProductionRevision = z.infer<typeof revisionSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Eylem istekleri — REST ve sohbet aracı AYNI şemayı kullanır
// ═══════════════════════════════════════════════════════════════════════════

/// İyimser eşzamanlılık: düzenleme anındaki `version`. Verilmezse kontrol
/// yok (sohbet aracı çoğu zaman bilmiyor) — verilirse bayatta 409.
const versionField = z.number().int().min(0).optional();

export const productionAnswersSchema = z.object({
  answers: z
    .array(
      z
        .object({
          questionId: shortIdSchema,
          optionId: shortIdSchema.optional(),
          text: z.string().trim().min(1).max(300).optional(),
        })
        .refine((answer) => Boolean(answer.optionId) || Boolean(answer.text), {
          message: 'optionId ya da text gerekli.',
          path: ['optionId'],
        }),
    )
    .min(1)
    .max(PRODUCTION_QUESTION_COUNT.max),
  version: versionField,
});
export type ProductionAnswersInput = z.infer<typeof productionAnswersSchema>;

export const productionPickConceptSchema = z.object({
  conceptId: shortIdSchema,
  version: versionField,
});
export type ProductionPickConceptInput = z.infer<typeof productionPickConceptSchema>;

/**
 * Senaryo düzenleme — KISMİ. `beats` verilirse liste BÜTÜNÜYLE değişir
 * (sıra ve kimlikler istemcinin), diğer bloklar alan alan birleşir.
 *
 * `acceptScript: true` — DIRECT'in senaryo kapısını (`SCRIPT_EDIT`) AÇIKÇA
 * kapatır ("bu senaryoyla devam"); tek başına gönderilebilir (değişiklik yok).
 * Önceden kapı boş bir CTA yamasıyla (`cta: {}`) kapatılıyordu (sohbet aracı).
 * Kapı dışında `acceptScript` tek başına anlamsız — servis reddeder.
 */
export const productionEditScriptSchema = z
  .object({
    beats: z.array(scriptBeatSchema).min(1).max(24).optional(),
    music: scriptMusicSchema.partial().optional(),
    voiceover: scriptVoiceoverSchema.partial().optional(),
    cta: scriptCtaSchema.partial().optional(),
    acceptScript: z.literal(true).optional(),
    version: versionField,
  })
  .refine(
    (patch) =>
      Boolean(patch.beats || patch.music || patch.voiceover || patch.cta || patch.acceptScript),
    { message: 'En az bir alan değişmeli.' },
  );
export type ProductionEditScriptInput = z.infer<typeof productionEditScriptSchema>;

/**
 * ÇIKTI BİÇİMİNİ DEĞİŞTİR (onay ekranı / senaryo kapısı) — AI'ın kararı
 * (`deliverablePlanSchema`) kullanıcının seçimiyle değişir: `{strategy,
 * variantCount}`. Açılış varyantları ve fikirler sunucuda seçilir (fikirler
 * sıralı: önerilen, sonra en az klişe); fiyat yeniden hesaplanır ve onay
 * ekranında "en fazla X" yeni sayıyı gösterir (`ProductionDetail.strategyQuotes`
 * değişiklikten ÖNCE her seçeneğin fiyatı). Onaydan sonra yok — biçim parayla
 * çekilen take'leri belirliyor; değişiklik revizyonla.
 */
export const productionSetStrategySchema = z
  .object({
    strategy: productionStrategySchema,
    variantCount: z.number().int().min(1).max(PRODUCTION_MAX_VARIANTS),
    version: versionField,
    /**
     * TASLAKTAN ÖNCE (storyboard rezervi açıkken, senaryo kapısı) AI zarfını
     * aşan biçimin EK taslak kredisine rıza (Dalga 5, kullanıcı kararı "birkaç
     * fikri yalnız kullanıcı seçer, ek tutar gösterilip onay alınır"). Ek tutar
     * > 0 ise ZORUNLU ve EŞİT olmalı; yoksa/tutmuyorsa 409 `PRICE_CHANGED`
     * (`details.expected` = ek kredi) — sohbet aracı sayıyı önce böyle öğrenir.
     * Ekranda önceden: `ProductionDetail.draftQuotes[].extraCredits`. Onay
     * ekranında (taslak hazır) yok sayılır — orada ek iş onay tutarına girer.
     */
    confirmCredits: z.number().int().min(0).optional(),
  })
  .refine(
    (input) =>
      input.variantCount >= PRODUCTION_STRATEGY_LIMITS[input.strategy].min &&
      input.variantCount <= PRODUCTION_STRATEGY_LIMITS[input.strategy].max,
    { message: 'Bu biçim için varyant sayısı geçersiz.', path: ['variantCount'] },
  );
export type ProductionSetStrategyInput = z.infer<typeof productionSetStrategySchema>;

/**
 * Storyboard düzenleme (DIRECT) — işlem listesi. Yalnız etkilenen çekim
 * yeniden üretilir: `edit` spec'i yamalar (`specVersion` artar), `delete`
 * çekimi DROPPED yapar, `add` yeni spec ekler, `reorder` sırayı bütünüyle verir.
 */
export const productionShotOpSchema = z.discriminatedUnion('op', [
  z.object({ op: z.literal('reorder'), order: z.array(z.uuid()).min(1).max(60) }),
  z.object({ op: z.literal('delete'), shotId: z.uuid() }),
  z.object({ op: z.literal('edit'), shotId: z.uuid(), patch: shotSpecSchema.omit({ order: true }).partial() }),
  z.object({
    op: z.literal('add'),
    /// Bu çekimden SONRA eklenir; `null` = başa.
    afterShotId: z.uuid().nullable(),
    spec: shotSpecSchema.omit({ order: true }),
  }),
]);
export type ProductionShotOp = z.infer<typeof productionShotOpSchema>;

export const productionEditShotsSchema = z.object({
  ops: z.array(productionShotOpSchema).min(1).max(30),
  version: versionField,
});
export type ProductionEditShotsInput = z.infer<typeof productionEditShotsSchema>;

export const productionReviseSchema = z.object({
  instruction: z.string().trim().min(1).max(500),
  /// Kullanıcı yeri biliyorsa ("3. sahne") yönlendiriciye ipucu; zorunlu değil.
  scope: z
    .object({
      stage: productionStageSchema.optional(),
      shotIds: z.array(z.uuid()).min(1).max(60).optional(),
    })
    .optional(),
  version: versionField,
});
export type ProductionReviseInput = z.infer<typeof productionReviseSchema>;

/// Kredi rızası: kullanıcının EKRANDA gördüğü üst sınır. Güncel tavan bundan
/// büyükse 409 `PRICE_CHANGED` — görmediği bir tutar kesilmez.
const confirmCreditsField = z.number().int().min(0);

export const productionConfirmRevisionSchema = z.object({
  revisionId: shortIdSchema,
  confirmCredits: confirmCreditsField,
});
export type ProductionConfirmRevisionInput = z.infer<typeof productionConfirmRevisionSchema>;

/**
 * Maliyetli revizyonu REDDET (`REVISION_CONFIRM` kapısının ikinci çıkışı).
 * Önceden kapının tek çıkışı ödemek ya da iptaldi: rıza vermeyen (ya da
 * bakiyesi yetmeyen) kullanıcı, parası ödenmiş filmine dönemiyordu ve yeni
 * revizyon da "önceki sonuçlanmadı" diye reddediliyordu.
 */
export const productionDeclineRevisionSchema = z.object({
  revisionId: shortIdSchema,
});
export type ProductionDeclineRevisionInput = z.infer<typeof productionDeclineRevisionSchema>;

export const productionApproveSchema = z.object({
  confirmCredits: confirmCreditsField,
  version: versionField,
});
export type ProductionApproveInput = z.infer<typeof productionApproveSchema>;

export const productionChooseTakeSchema = z.object({
  shotId: z.uuid(),
  takeId: z.uuid(),
});
export type ProductionChooseTakeInput = z.infer<typeof productionChooseTakeSchema>;

/**
 * Başarısız (ya da beğenilmeyen) çekimi yeniden dene — talimat kusur
 * listesiyle birlikte prompt'a eklenir (talimatlı deneme bir revizyon olur).
 *
 * `confirmCredits` — talimatSIZ deneme YENİ bir kredi rızası (kullanıcı
 * kararı, 1 Eki 2026; `productionRetryConsentSchema` başlığı). Ekranda
 * gösterilen "en fazla X" (`ProductionShotView.retry.maxCredits`) EŞİTLİKLE
 * gönderilir; yoksa ya da tutmuyorsa 409 `PRICE_CHANGED` + güncel sayılar —
 * sohbet aracı sayıyı önce böyle öğrenip kullanıcıya gösterebilir.
 */
export const productionRetryShotSchema = z.object({
  shotId: z.uuid(),
  instruction: z.string().trim().min(1).max(300).optional(),
  confirmCredits: confirmCreditsField.optional(),
});
export type ProductionRetryShotInput = z.infer<typeof productionRetryShotSchema>;

/**
 * KULLANICI YENİDEN ÇEKİM RIZASI — `CreativeProduction.retries` (JSON dizisi).
 *
 * Onaydaki en kötü durum rezervi çekim başına BİR kurtarma turunu kapsıyor
 * (take sayısı + `PRODUCTION_MAX_REROLLS` yeniden çekim + onarım). Kullanıcının
 * onaydan sonra açtığı yeniden deneme o rızanın DIŞINDA: kendi rezervini açar
 * (`productionReservationKey(id, {retry: {shotId, n}}, 'v<sürüm>')`) ve teslimde
 * diğer rezervlerle aynı yoldan gerçek maliyetle kapanır, kalanı iade. Önceden
 * deneme rezervsizdi ve tavanı (2 tur) aşan kısım bizde kalıyordu; tavan bugün
 * de GÜVENLİK sınırı olarak duruyor.
 *
 * Kapanış rezervi BURADAN buluyor (`reservationId` — anahtar deneme eki
 * taşıdığı için numaradan yeniden kurulamaz). USD yok: kredi rızanın kendisi.
 */
export const productionRetryConsentSchema = z.object({
  shotId: z.uuid(),
  /// Bu çekimin kaçıncı kullanıcı denemesi (1'den).
  n: z.number().int().min(1).max(100),
  /// Denemenin yapıldığı spec — düzenlenen çekimin eski rızası ayırt edilsin.
  specVersion: z.number().int().min(0),
  /// Rıza verilen tavan = rezervin tutarı.
  credits: z.number().int().min(0),
  reservationId: z.string().min(1).max(64).nullable(),
  createdAt: z.iso.datetime(),
});
export type ProductionRetryConsent = z.infer<typeof productionRetryConsentSchema>;

/**
 * BİÇİM RIZASI — `CreativeProduction.planConsents` (JSON dizisi, Dalga 5).
 *
 * Başlarken kilitlenen storyboard rezervi yalnız AI'ın kendi seçebileceği
 * biçimleri kapsıyor (`PRODUCTION_AUTO_STRATEGY_LIMITS` → `autoPlanShapes`).
 * Kullanıcı TASLAKTAN ÖNCE daha büyük bir biçim seçerse (birkaç fikir, 4–5
 * açılış) ek taslak işinin (fikir başına senaryo + çekim listesi, ek kareler)
 * tahmini zarfın ÜSTÜNDE kalıyordu ve rezerv kapanırken bizde kalıyordu
 * (Dalga 4 #12 bu yüzden 400 dönüyordu). Artık: ek kredi gösterilir, rıza
 * EŞİTLİKLE alınır (`productionSetStrategySchema.confirmCredits`) ve ayrı bir
 * rezerv açılır (`productionReservationKey(id, { plan: n }, 'v<sürüm>')`).
 *
 * Kapanış storyboard rezerviyle BİRLİKTE, gerçek maliyetle, sırayla (önce
 * storyboard, sonra biçim rızaları) — kullanılmayan kısım iade; terminal hata
 * TAM iade, iptal o ana kadarki gerçek maliyet (diğer rezervlerle aynı yol).
 * Kapanış rezervi BURADAN buluyor (`reservationId`; anahtar deneme eki taşıyor).
 */
export const productionPlanConsentSchema = z.object({
  /// Bu prodüksiyonun kaçıncı biçim rızası (1'den).
  n: z.number().int().min(1).max(100),
  /// Rızanın verildiği biçim.
  strategy: productionStrategySchema,
  variantCount: z.number().int().min(1).max(PRODUCTION_MAX_VARIANTS),
  /// Rıza verilen EK kredi = rezervin tutarı.
  credits: z.number().int().min(1),
  reservationId: z.string().min(1).max(64).nullable(),
  /// Rızanın verildiği prodüksiyon sürümü (rezerv anahtarının deneme eki).
  version: z.number().int().min(0),
  createdAt: z.iso.datetime(),
});
export type ProductionPlanConsent = z.infer<typeof productionPlanConsentSchema>;

/// İptal: o ana kadarki GERÇEK maliyet kesilir, kalanı iade (plan §2.6).
export const productionCancelSchema = z.object({
  reason: z.string().trim().max(200).optional(),
});
export type ProductionCancelInput = z.infer<typeof productionCancelSchema>;

export const productionListQuerySchema = listQuerySchema.extend({
  status: productionStatusSchema.optional(),
  /// Yalnız kullanıcı bekleyenler (kapısı açık olanlar).
  awaiting: boolParam.optional(),
  /// Yalnız SÜRENLER (terminal olmayan: DELIVERED/FAILED/CANCELED hariç) —
  /// tezgâhın kuyruğu ve "devam eden reklamlar" listesi.
  active: boolParam.optional(),
  productId: z.uuid().optional(),
  /// Yalnız bu tür (Dalga 5) — geçmişin "Reklam videosu" / "Görsel kampanya" süzgeci.
  kind: productionKindSchema.optional(),
  sort: z.enum(['createdAt', 'updatedAt']).default('updatedAt'),
  dir: sortDirSchema,
});
export type ProductionListQuery = z.infer<typeof productionListQuerySchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Yanıt projeksiyonları — USD YOK, prompt YOK, imzalı adres var
// ═══════════════════════════════════════════════════════════════════════════

/// Kredi tahmini — ekranda "en fazla X, tahmini Y".
export interface ProductionEstimateView {
  storyboardCredits: number;
  production: { expectedCredits: number; maxCredits: number } | null;
}

/**
 * `POST /creatives/productions/estimate` — "Başlat"tan ÖNCE ekrandaki sayılar.
 *
 * `storyboardCredits` başlarken REZERVE edilecek tutarın kendisi. `production`
 * burada GÖSTERGE (`productionIndicative: true`): çekim listesi henüz yok,
 * tipik çekim karışımıyla (REPORT §2.5 oranı) hesaplandı; onayda gerçek
 * çekimlerle yeniden hesaplanır ve rıza o sayıya istenir.
 */
export interface ProductionEstimateResponse extends ProductionEstimateView {
  production: { expectedCredits: number; maxCredits: number };
  productionIndicative: true;
  /// Tahminin kurulduğu süre (`durationSeconds: null` → yerleşimin varsayılanı) ve çekim sayısı.
  durationSeconds: number;
  shotCount: number;
  available: number;
  /// Bakiye storyboard rezervine yetiyor mu (üretim rezervi onayda istenir).
  sufficient: boolean;
  topupPath: string | null;
}

export interface ProductionCreditsView {
  /// Şu an kilitli (açık rezervasyonların toplamı).
  reserved: number;
  /// Kesinleşmiş (`creditsCharged`).
  charged: number;
  /// İade edilmiş (kapanan rezervlerin kullanılmayan kısmı + başarısızlık
  /// iadesi) — "X kredi iade edildi" cümlesi. Defterden (REFUND satırları).
  refunded: number;
}

/**
 * ÇIKTI BİÇİMİ SEÇENEĞİNİN FİYATI — onay ekranında "3 açılış varyantı · en
 * fazla X" (`productionSetStrategySchema` öncesi). Güncel çekimlerden; yeni
 * varyantın taslak kareleri ve yazımı dahil. Onaydan sonra yok.
 */
export interface ProductionStrategyQuote {
  strategy: ProductionStrategy;
  variantCount: number;
  expectedCredits: number;
  maxCredits: number;
  /// Planın şu anki biçimi bu mu.
  current: boolean;
}

/**
 * TASLAKTAN ÖNCE biçim seçeneğinin EK taslak kredisi (Dalga 5) — senaryo
 * kapısında (storyboard rezervi açıkken) `strategyQuotes` yok (çekim yok, onay
 * fiyatı bilinmiyor); seçeneğin şimdi rıza isteyen kısmı bu. `extraCredits` 0 =
 * başta kilitlenen tutar (ve önceki biçim rızaları) kapsıyor, rıza gerekmez;
 * > 0 = `setStrategy` bu sayıyla `confirmCredits` ister ve ayrı rezerv açar.
 */
export interface ProductionDraftQuote {
  strategy: ProductionStrategy;
  variantCount: number;
  extraCredits: number;
  /// AI bu biçimi kendi başına seçebilir mi (`productionAutoStrategyAllowed`).
  auto: boolean;
  current: boolean;
}

/// Varyantın ekrandaki hâli — plan + çekimleri + (açılışta) alternatif açılış.
export interface ProductionVariantView {
  key: ProductionVariantKey;
  kind: ProductionVariantKind;
  conceptId: string;
  label: string;
  rationale: string;
  hookBeatId: string | null;
  /// Açılış varyantının açılışı (beat içeriği) — ekranda "açılış" kartı.
  hook: { description: string; super: string | null } | null;
  /**
   * Çok fikirli setin `v2…` fikrinin KENDİ senaryosu (reklamın akışı) — senaryo
   * kapısından önce yazılıyor; ekran yalnız Fikir A'yı gösteriyordu ve "Bu
   * akışla devam" görmediği Fikir B'yi de onaylatıyordu. `v1` ve açılış
   * varyantlarında yok (birincil senaryo `ProductionDetail.script`).
   */
  script?: ProductionScript | null;
  /// Bu varyantın filmindeki etkin çekimler (ortak + kendi), sırayla.
  shotIds: string[];
}

export interface ProductionProgress {
  stage: ProductionStage;
  /// Aşamanın kendi birimi: çekim aşamasında biten çekim, storyboard'da kare…
  done: number;
  total: number;
}

export interface ProductionListRow {
  id: string;
  /// Reklam videosu ya da görsel kampanya (Dalga 5).
  kind: ProductionKind;
  /// Görsel kampanyanın biçimi; videoda `null`.
  setType: ImageSetType | null;
  status: ProductionStatus;
  stage: ProductionStage;
  gate: ProductionGate | null;
  mode: ProductionMode;
  aspect: ProductionAspect;
  placement: ProductionPlacement | null;
  durationSeconds: number | null;
  /// Seçilen konseptin başlığı; yoksa ürün adı.
  title: string | null;
  product: { id: string | null; name: string | null; thumbUrl: string | null };
  progress: ProductionProgress;
  credits: ProductionCreditsView;
  /// Teslimdeki `master` kreatif — geçmiş kartı bunu açar.
  masterCreativeId: string | null;
  error: { code: string } | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  /// PROVA prodüksiyonu (Faz 10a) — yalnız `true` iken var; ekran "Prova" der.
  rehearsal?: true;
  /// SAĞLAYICI BEKLEMESİ (3 Eki 2026) — yalnız beklerken var; kuyruk satırı
  /// "Beklemede — sağlayıcı kapasitesi" der (hata tonu yok, kullanıcının suçu değil).
  providerWait?: { since: string };
}

export interface ProductionQuestionView {
  id: string;
  text: string;
  options: { id: string; label: string; previewUrl: string | null }[];
  answerOptionId: string | null;
  answerText: string | null;
}

export interface ProductionKeyframeView {
  url: string | null;
  candidates: { url: string; qa: TakeQa | null }[];
}

export interface ProductionTakeView {
  id: string;
  index: number;
  reason: ProductionTakeReason;
  status: ProductionTakeStatus;
  url: string | null;
  posterUrl: string | null;
  qa: TakeQa | null;
}

export interface ProductionShotView {
  id: string;
  order: number;
  status: ProductionShotStatus;
  spec: ShotSpec;
  specVersion: number;
  keyframe: ProductionKeyframeView | null;
  takes: ProductionTakeView[];
  chosenTakeId: string | null;
  /// Talimatsız yeniden çekimin kredi rızası — "en fazla X, tahmini Y".
  /// Yalnız onaydan sonra ve çekim HAZIR ya da BAŞARISIZ iken; yoksa null.
  retry: { expectedCredits: number; maxCredits: number } | null;
  /// Çekim satırının hata kodu (`KEYFRAME_FAILED` / `SHOT_FAILED`) — ekran
  /// seçenekleri buna göre sunar (karesi tükenen çekim yeniden ÇEKİLEMEZ).
  errorCode: string | null;
  /// Hangi varyantların filminde (boş = hepsinin ortak çekimi).
  variantKeys: ProductionVariantKey[];
}

export interface ProductionAudioView {
  kind: 'MUSIC' | 'VOICEOVER' | 'SFX' | 'MIX';
  url: string;
  durationMs: number | null;
}

export interface ProductionDeliverableView {
  creativeId: string;
  rendition: string;
  /// Hangi kurgunun teslimi (`productionRenditionCut`; 0 = ilk teslim).
  cut: number;
  /// Hangi varyantın teslimi (`productionRenditionVariant`; ekyok = `v1`).
  variantKey: ProductionVariantKey;
  /**
   * Görsel kampanya teslimi (`parseImageSetRendition`): serinin/karuselin kaçıncı
   * karesi, banner setinin hangi boyutu. Videoda ikisi de `null`. Banner'da
   * `aspect` boyutun EN YAKIN prodüksiyon oranı (`BANNER_SET_SIZES[].nearestAspect`).
   */
  frameIndex: number | null;
  bannerSize: BannerSizeId | null;
  aspect: ProductionAspect;
  url: string | null;
  posterUrl: string | null;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  /// Eksik parçayla teslim (ör. müziksiz) — boş = tam.
  warnings: ProductionDeliverableWarning[];
}

export interface ProductionRevisionView {
  id: string;
  instruction: string;
  status: ProductionRevisionStatus;
  summary: string | null;
  /// Onay sonrası maliyetli revizyonun ek kredisi; yoksa null.
  costDeltaCredits: number | null;
  /// `rejected` ise sebebi (i18n anahtarının son parçası); değilse null.
  rejectedReason: ProductionRevisionRejectReason | null;
  /// Teslimden sonra istendi — uygulanırsa yeni kurgu açar.
  postDelivery: boolean;
  createdAt: string;
}

export interface ProductionDetail {
  id: string;
  /// Reklam videosu ya da görsel kampanya (Dalga 5).
  kind: ProductionKind;
  status: ProductionStatus;
  stage: ProductionStage;
  gate: ProductionGate | null;
  mode: ProductionMode;
  /// Listedekiyle aynı: seçilen fikrin başlığı, yoksa ürün adı.
  title: string | null;
  /// Ürün (katalog ya da yüklenen ilk fotoğraf) — başlıktaki küçük resim.
  product: { id: string | null; name: string | null; thumbUrl: string | null };
  /// Başlatma isteğinin kendisi (varsayılanlar oturmuş).
  input: ProductionStartInput;
  /// Çıktı biçimi (strateji adımından sonra) — yoksa null.
  deliverablePlan: DeliverablePlan | null;
  /// Planın varyantları ekran hâliyle (plan yoksa, senaryo varsa tek `v1`).
  variants: ProductionVariantView[];
  /// Biçim seçeneklerinin fiyatı — yalnız onaydan önce, plan ve çekimler varken.
  strategyQuotes: ProductionStrategyQuote[] | null;
  /**
   * Taslaktan ÖNCE (senaryo kapısı, storyboard rezervi açık) biçim seçeneklerinin
   * EK taslak kredisi — `setStrategy`in `confirmCredits` istediği sayı. Taslak
   * hazırsa, görsel kampanyada ya da plan yoksa `null`.
   */
  draftQuotes: ProductionDraftQuote[] | null;
  /// Görsel kampanyanın isteği (set türü, kare sayısı / boyutlar); videoda `null`.
  imageSet: ImageSetRequest | null;
  /// Görsel kampanyanın kare planı (`SET_PLAN` sonrası); videoda / öncesinde `null`.
  setPlan: ImageSetPlan | null;
  dossier: ProductionDossierPublic | null;
  questions: ProductionQuestionView[];
  concepts: ProductionConcept[];
  chosenConceptId: string | null;
  script: ProductionScript | null;
  shots: ProductionShotView[];
  audio: ProductionAudioView[];
  deliverables: ProductionDeliverableView[];
  revisions: ProductionRevisionView[];
  estimate: ProductionEstimateView | null;
  credits: ProductionCreditsView;
  progress: ProductionProgress;
  /// Güncel kurgu (`productionCutIndex`) — 0 = ilk teslim; teslimden sonraki
  /// her uygulanmış revizyon bir artırır. Teslimler `cut` alanıyla gruplanır.
  cut: number;
  /// Geçmişte görünen teslim: EN SON teslim edilen kurgunun master'ı.
  masterCreativeId: string | null;
  error: { code: string } | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  startedAt: string | null;
  storyboardReadyAt: string | null;
  approvedAt: string | null;
  /// GÜNCEL kurgunun teslim anı — yeni kurgu sürerken null (önceki kurgunun
  /// teslimleri `deliverables`ta durur).
  deliveredAt: string | null;
  canceledAt: string | null;
  /// PROVA prodüksiyonu (Faz 10a) — yalnız `true` iken var: medya yedek, QA
  /// "geçti (prova)", kredi tam iade. Ekran ve sohbet "Prova" etiketi gösterir.
  rehearsal?: true;
  /**
   * SAĞLAYICI BEKLEMESİ (3 Eki 2026, `productionProviderWaitOf`) — yalnız
   * beklerken var. Prodüksiyon olduğu aşamada duruyor, rezerv açık, biten
   * çekimler korunuyor; sağlayıcı açılınca kendiliğinden sürer. Ekran sakin
   * bir not gösterir — kullanıcının bakiyesini SUÇLAMAZ.
   */
  providerWait?: { since: string };
}

// ═══════════════════════════════════════════════════════════════════════════
// A/B yayını isteği (Dalga 5 şerit B3; Dalga 5b Delta'da API'den taşındı)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * "A/B olarak yayınla" — varyant setini TEK reklam setine ayrı reklamlar
 * olarak. Panel (`POST /creatives/productions/:id/ab-publish(/plan)`) ve
 * sohbet aracı (`production_publish_ab`) AYNI şema; planın kendisi (katalog
 * operasyonları, rol kapısı) API `creative/production/publish/ab-publish.plan.ts`.
 * Reklamlar DURAKLATILMIŞ doğar; bu istek para harcamaz.
 */
export const abPublishRequestSchema = z.object({
  /// Mentorivo reklam seti (Meta ad set / TikTok ad group) kimliği.
  adSetId: z.uuid(),
  /**
   * Reklamın kimliği — Meta: Facebook Sayfası kimliği; TikTok: kimlik referansı
   * (`TT_USER:<id>`, `BC_AUTH_TT:<bc>:<id>`). İkisi de
   * `GET /integrations/connections/:id/identities` listesinden.
   */
  identityRef: z.string().trim().min(1).max(200),
  /// Reklam metni — her varyantta AYNI (test edilen tek değişken video).
  copy: z.object({
    text: z.string().trim().min(1).max(2000),
    headline: z.string().trim().min(1).max(255).optional(),
    cta: z.string().trim().min(1).max(40).optional(),
    link: z.url().max(2000).optional(),
  }),
  /// Hangi boyutun teslimi (varsayılan: prodüksiyonun ana boyutu).
  aspect: productionAspectSchema.optional(),
  /// Alt küme (varsayılan: teslimi olan bütün varyantlar). En az iki.
  variants: z
    .array(productionVariantKeySchema)
    .min(2)
    .max(PRODUCTION_VARIANT_KEYS.length)
    .optional(),
  /// Platformun temiz A/B testi (yalnız Meta) — gün.
  splitTest: z.object({ days: z.number().int().min(3).max(30).default(7) }).optional(),
});
export type AbPublishRequest = z.infer<typeof abPublishRequestSchema>;
